/**
 * supabaseStore.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Drop-in async replacement for store.js — backed by Supabase.
 * Automatically falls back to the localStorage store when
 * VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are not configured.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { MAX_DOC_BYTES, mimeForFile, assertSupportedDocument } from './documentUploads.js';
import { supabase, isSupabaseEnabled } from '../lib/supabase';
import {
  ALLOWED_ATTACHMENT_MIME, MAX_ATTACHMENT_BYTES, chatAttachmentPath,
  pastedAttachmentName, safeAttachmentName,
} from './chatAttachments';
import { blankToNull } from './dealDbColumns';
import { store as localStore } from './store';
import { fetchAllRows as pagedFetchAllRows } from './pagedSelect';
import {
  dbDocToApp,
  dbContactToApp,
  dbDealToApp,
  dbSignatureRequestToApp,
  dbCertifiedMailJobToApp,
  dbBuyerToApp,
  dbInvestorToApp,
  dbInvestorEntityToApp,
  dbCertifiedMailDocumentToApp,
  dbCertifiedMailEventToApp,
  dbUserToApp,
  dbTodoToApp,
  dbDealStateSummaryToApp,
} from './dbRows.js';
export {
  dbDocToApp,
  dbContactToApp,
  dbDealToApp,
  dbCertifiedMailJobToApp,
  dbBuyerToApp,
  dbInvestorToApp,
  dbInvestorEntityToApp,
  dbCertifiedMailDocumentToApp,
  dbCertifiedMailEventToApp,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function handleError(label, error) {
  console.error(`[supabaseStore] ${label}:`, error?.message || error);
  throw new Error(`${label}: ${error?.message || 'Unknown error'}`);
}

function isMissingTableError(error, tableName) {
  if (!error || !tableName) return false;
  const haystack = [
    error.code,
    error.message,
    error.details,
    error.hint,
  ].filter(Boolean).join(' ').toLowerCase();
  const table = String(tableName).toLowerCase();
  return (
    error.code === '42P01'
    || error.code === 'PGRST205'
    || haystack.includes(`public.${table}`)
    || haystack.includes(`"${table}"`)
    || (haystack.includes('schema cache') && haystack.includes(table))
  );
}

function handleMissingTaxTableOrThrow(label, error, tableName) {
  if (isMissingTableError(error, tableName)) {
    throw new Error(`Tax Forms table "${tableName}" is missing in Supabase. Run supabase/migration-044-tax-automation.sql, then refresh the app.`);
  }
  handleError(label, error);
}

function isOnConflictConstraintError(error) {
  const haystack = [
    error?.code,
    error?.message,
    error?.details,
    error?.hint,
  ].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes('no unique or exclusion constraint matching the on conflict specification');
}

// Paged reads. See pagedSelect.js for why unbounded .select() is a
// correctness bug here. Every caller below orders by a business column
// *and* `id`, because paging is only sound under a total order.
function fetchAllRows(label, buildQuery, opts = {}) {
  return pagedFetchAllRows(label, buildQuery, {
    ...opts,
    onError: (error) => handleError(label, error),
  });
}

const CRITICAL_DEAL_AUDIT_COLUMNS = new Set([
  'stage',
  'lifecycle',
  'archived',
  'offer_amount',
  'asking_price',
  'price_per_nma',
  'buy_closing_date',
  'sell_closing_date',
  'closing_date',
  'buy_side_agent_id',
  'sell_side_agent_id',
  'disposition_buyer_id',
  // Placing a deal with an investor moves who takes title and who
  // earns on it. That belongs in the audit trail for the same reason
  // the buyer and the agents do.
  'investor_id',
  'investor_entity_id',
  'investor_capital',
  'attached_document_ids',
  'closing_docs',
  'wiring_info',
]);

function auditId() {
  return `audit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

let auditEventsUnavailable = false;

function hasCriticalDealAuditColumn(patch = {}) {
  return Object.keys(patch).some((key) => CRITICAL_DEAL_AUDIT_COLUMNS.has(key));
}

function auditActionForDealPatch(patch = {}) {
  if ('stage' in patch) return 'deal.stage_changed';
  if ('archived' in patch) return patch.archived ? 'deal.archived' : 'deal.unarchived';
  if ('closing_docs' in patch || 'attached_document_ids' in patch) return 'deal.documents_changed';
  if ('wiring_info' in patch || 'buy_closing_date' in patch || 'sell_closing_date' in patch || 'closing_date' in patch) return 'deal.closing_changed';
  if ('investor_id' in patch || 'investor_entity_id' in patch || 'investor_capital' in patch) return 'deal.investor_placed';
  if ('disposition_buyer_id' in patch || 'buy_side_agent_id' in patch || 'sell_side_agent_id' in patch) return 'deal.parties_changed';
  if ('offer_amount' in patch || 'asking_price' in patch || 'price_per_nma' in patch) return 'deal.pricing_changed';
  return 'deal.updated';
}

async function recordAuditEvent(event = {}) {
  const uid = await currentUserId();
  const tenantId = await currentTenantId();
  if (!tenantId) throw new Error('Your profile is missing a tenant id. Audit event was not saved.');
  const row = {
    id: event.id || auditId(),
    tenant_id: tenantId,
    actor_id: event.actorId || uid || null,
    action: event.action,
    entity_type: event.entityType,
    entity_id: event.entityId ? String(event.entityId) : null,
    deal_id: event.dealId ? String(event.dealId) : null,
    before_data: event.beforeData || null,
    after_data: event.afterData || null,
    metadata: event.metadata && typeof event.metadata === 'object' ? event.metadata : {},
    created_at: event.createdAt || new Date().toISOString(),
  };
  const { error } = await supabase.from('audit_events').insert(row);
  if (error) {
    if (isMissingTableError(error, 'audit_events')) {
      throw new Error(error.message || 'audit_events table is missing');
    }
    handleError('recordAuditEvent', error);
  }
  return row.id;
}

async function recordAuditEventQuietly(event = {}) {
  if (!isSupabaseEnabled || !event?.action || !event?.entityType) return null;
  if (auditEventsUnavailable) return null;
  try {
    return await recordAuditEvent(event);
  } catch (err) {
    if (isMissingTableError(err, 'audit_events')) {
      auditEventsUnavailable = true;
      console.warn('[supabaseStore] audit_events table is unavailable; audit logging is paused until migration-108/110/118 is run.');
    } else {
      console.warn('[supabaseStore] audit event skipped:', err?.message || err);
    }
    return null;
  }
}

// Cache the user ID to avoid concurrent getUser() calls fighting over the auth lock
let _cachedUid = null;
async function currentUserId() {
  if (_cachedUid) return _cachedUid;
  // Use getSession() first — it reads from local storage and never hangs on navigator.locks
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session?.user?.id) {
      _cachedUid = sessionData.session.user.id;
      return _cachedUid;
    }
  } catch { /* fall through to getUser */ }
  // Fallback to getUser() with a timeout to prevent hanging
  try {
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Auth getUser timeout')), 5000)
    );
    const { data } = await Promise.race([supabase.auth.getUser(), timeout]);
    _cachedUid = data?.user?.id ?? null;
  } catch (err) {
    if (err?.name === 'AbortError' || err?.message?.includes('Lock broken') || err?.message?.includes('timeout')) {
      // Last resort: try session again
      const { data: s } = await supabase.auth.getSession();
      _cachedUid = s?.session?.user?.id ?? null;
    } else {
      throw err;
    }
  }
  return _cachedUid;
}
// Clear cache on auth state change
let _cachedTenantId = null;
async function currentTenantId() {
  if (_cachedTenantId) return _cachedTenantId;
  const uid = await currentUserId();
  if (!uid) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('tenant_id')
    .eq('id', uid)
    .maybeSingle();
  if (error) throw new Error(`Tenant lookup failed: ${error.message}`);
  _cachedTenantId = data?.tenant_id || null;
  return _cachedTenantId;
}

async function tenantDocumentPath(...parts) {
  const tenantId = await currentTenantId();
  if (!tenantId) throw new Error('Your profile is missing a tenant id. Ask an admin to refresh your user profile before uploading documents.');
  const cleanParts = parts
    .map((part) => String(part || '').replace(/^\/+|\/+$/g, ''))
    .filter(Boolean);
  return ['tenants', tenantId, ...cleanParts].join('/');
}

async function assertTenantStoragePath(storagePath) {
  if (!storagePath) return null;
  const tenantId = await currentTenantId();
  if (!tenantId) throw new Error('Your profile is missing a tenant id. Ask an admin to refresh your user profile before opening documents.');
  const normalized = String(storagePath || '').replace(/^\/+/, '');
  const allowedPrefix = `tenants/${tenantId}/`;
  if (!normalized.startsWith(allowedPrefix)) {
    throw new Error('Blocked document storage access outside your tenant workspace.');
  }
  return normalized;
}

async function assertReadableDocumentStoragePath(storagePath) {
  if (!storagePath) return null;
  const normalized = String(storagePath || '').replace(/^\/+/, '');
  if (!normalized.startsWith('tenants/')) {
    return normalized;
  }
  return assertTenantStoragePath(normalized);
}

supabase?.auth?.onAuthStateChange(() => { _cachedUid = null; _cachedTenantId = null; });

// ── Date sanitization — reject invalid dates before they hit the DB ───────────
function sanitizeDate(val) {
  if (!val) return null;
  const s = String(val).trim();
  if (!s || s === '—' || s.includes('--') || s.includes('//')) return null;
  // Try parsing — must produce a valid date
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  // Return ISO date string (YYYY-MM-DD) for Supabase date columns
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return d.toISOString().slice(0, 10);
}

// documents.name is NOT NULL, so a caller that computes a blank name
// (a recorded instrument whose book/page didn't parse, say) would get an
// opaque 400 from PostgREST. Fall back through the fields that carry a
// human label before giving up on a generic one — a document that saves
// under a rough name can be renamed; one that 400s is just lost.
function documentDisplayName(doc = {}) {
  const candidates = [doc.name, doc.originalFilename, doc.instrumentTitle, doc.docType];
  for (const candidate of candidates) {
    const label = String(candidate ?? '').trim();
    if (label) return label;
  }
  return 'Document';
}

// ── Document file storage ─────────────────────────────────────────────────────

// Decodes the data URL, uploads the blob to the documents bucket, and
// logs a structured error object on failure (status, message, name,
// path, mime, size) so the console tells us which layer broke — not
// just the opaque "Failed to fetch" that the browser surfaces for
// transport-level failures.
// Limits applied client-side BEFORE upload. Mirrors api/_lib/validate.js
// (intentionally duplicated — frontend can't import from api/).

async function uploadDocumentFile(docId, dataUrl, fileName) {
  if (!dataUrl?.startsWith('data:')) {
    console.warn('[supabaseStore] uploadDocumentFile: invalid dataUrl');
    return null;
  }
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] || 'application/octet-stream';
  // Quick size gate using base64 length × 0.75 (decoded byte count).
  // Done before the atob() / Blob() construction so we don't burn
  // memory on a payload we're about to reject anyway.
  const approxBytes = Math.floor((base64?.length || 0) * 0.75);
  if (approxBytes > MAX_DOC_BYTES) {
    throw new Error(`File too large (${(approxBytes / 1024 / 1024).toFixed(1)} MB). Max 25 MB per document.`);
  }
  assertSupportedDocument({ mime, size: approxBytes, name: fileName || docId });
  // Indexed loop instead of Uint8Array.from(…, mapper): the mapper
  // variant invokes a closure per character and blocked the main
  // thread for seconds on 20MB+ PDFs.
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: mime });
  // Suffix the basename with a per-upload nonce so two concurrent
  // uploads with the same filename can't silently overwrite each
  // other (previously, both wrote the same path with `upsert: true`
  // and the second clobbered the first; the doc row of upload A then
  // pointed at upload B's bytes). docId is already unique per call,
  // so the per-upload nonce is belt-and-suspenders insurance against
  // any caller that recycles a docId.
  const rawSafe = (fileName || docId).replace(/[^a-zA-Z0-9._-]/g, '_');
  const dot = rawSafe.lastIndexOf('.');
  const stem = dot > 0 ? rawSafe.slice(0, dot) : rawSafe;
  const ext  = dot > 0 ? rawSafe.slice(dot)    : '';
  const nonce = Math.random().toString(36).slice(2, 8);
  const safeName = `${stem}_${nonce}${ext}`;
  const path = await tenantDocumentPath('documents', docId, safeName);
  try {
    const { data, error } = await supabase.storage
      .from('documents')
      .upload(path, blob, { upsert: false, contentType: mime });
    if (error) {
      const status = error.status ?? error.statusCode ?? null;
      console.error('[supabaseStore] upload failed', {
        path, mime, size: blob.size,
        error: { message: error.message, name: error.name ?? null, status },
      });
      if (!status && /failed to fetch/i.test(error.message || '')) {
        throw new Error('Upload did not reach Supabase (browser extension, VPN/proxy, or endpoint security).');
      }
      throw new Error(`Storage upload failed${status ? ` (HTTP ${status})` : ''}: ${error.message}`);
    }
    return data?.path ?? path;
  } catch (err) {
    console.error('[supabaseStore] uploadDocumentFile exception', {
      path, mime, size: blob.size,
      message: err?.message, name: err?.name,
    });
    throw err;
  }
}

/**
 * Upload a File/Blob directly to Supabase Storage (no base64 conversion).
 * Use this for bulk uploads to avoid GB of base64 strings in memory.
 */
async function uploadFileDirectly(docId, file) {
  try {
    const mime = mimeForFile(file);
    assertSupportedDocument({ mime, size: file?.size || 0, name: file?.name || docId });
    const rawSafe = (file.name || docId).replace(/[^a-zA-Z0-9._-]/g, '_');
    const dot = rawSafe.lastIndexOf('.');
    const stem = dot > 0 ? rawSafe.slice(0, dot) : rawSafe;
    const ext = dot > 0 ? rawSafe.slice(dot) : '';
    const nonce = Math.random().toString(36).slice(2, 8);
    const safeName = `${stem}_${nonce}${ext}`;
    const path = await tenantDocumentPath('documents', docId, safeName);
    const { data, error } = await supabase.storage
      .from('documents')
      .upload(path, file, { upsert: false, contentType: mime });
    if (error) {
      const status = error.status ?? error.statusCode ?? null;
      console.error('[supabaseStore] Storage direct upload error:', {
        path, mime, size: file?.size || 0,
        error: { message: error.message, name: error.name ?? null, status },
      });
      throw new Error(`Storage upload failed${status ? ` (HTTP ${status})` : ''}: ${error.message}`);
    }
    return data?.path ?? path;
  } catch (err) {
    console.error('[supabaseStore] uploadFileDirectly exception:', err);
    throw err;
  }
}

async function deleteStoragePath(storagePath) {
  if (!storagePath) return;
  const safePath = await assertTenantStoragePath(storagePath);
  const { error } = await supabase.storage.from('documents').remove([safePath]);
  if (error) throw new Error(`Storage cleanup failed: ${error.message}`);
}

/**
 * Batch insert multiple document rows in a single Supabase call.
 * Accepts an array of app-model doc objects (same shape as addDocument).
 * Storage paths must already be set on each doc.
 */
async function addDocumentsBatch(docs) {
  if (!docs.length) return;
  const uid = await currentUserId();
  const safeDocs = await Promise.all(docs.map(async (doc) => ({
    ...doc,
    storagePath: doc.storagePath ? await assertTenantStoragePath(doc.storagePath) : null,
  })));
  const rows = safeDocs.map(doc => ({
    id: doc.id,
    assignment_id: doc.assignmentId || null,
    deal_id: doc.dealId || null,
    interest_estimate_id: doc.interestEstimateId || null,
    name: documentDisplayName(doc),
    doc_type: doc.docType, instrument_title: doc.instrumentTitle,
    storage_path: doc.storagePath || null,
    file_size: typeof doc.size === 'number' ? doc.size : null, source: doc.source,
    uploaded_at: doc.uploadedAt || new Date().toISOString(), uploaded_by: uid,
    grantor: doc.grantor, grantee: doc.grantee,
    grantor_address: doc.grantorAddress || null,
    grantee_address: doc.granteeAddress || null,
    effective_date: sanitizeDate(doc.effectiveDate), file_date: sanitizeDate(doc.fileDate),
    volume: doc.volume, page: doc.page, instrument_no: doc.instrumentNo,
    legal_description: doc.legalDescription, original_filename: doc.originalFilename || null,
    state: doc.state || null,
    county: doc.county || null,
    // Runsheet fields (migration-029).
    section: doc.section || null,
    township: doc.township || null,
    range: doc.range || null,
    consideration: doc.consideration || null,
    runsheet_notes: doc.runsheetNotes || null,
  }));
  // Insert in chunks of 50 to stay under Supabase payload limits.
  // Each chunk retries with missing columns pruned if PostgREST rejects
  // a column that doesn't exist in the caller's DB (covers pre-migration
  // installs missing original_filename / deal_id / interest_estimate_id).
  for (let i = 0; i < rows.length; i += 50) {
    const chunk = rows.slice(i, i + 50);
    let done = false;
    for (let attempt = 0; attempt < 4 && !done; attempt++) {
      const pruned = chunk.map(pruneMissingDocFields);
      const { error } = await supabase.from('documents').insert(pruned);
      if (!error) { done = true; break; }
      if (!noteMissingDocColumnFromError(error)) {
        handleError('addDocumentsBatch', error);
        return;
      }
    }
    if (!done) handleError('addDocumentsBatch', new Error('Too many missing-column retries on addDocumentsBatch'));
  }
}

// Cache signed URLs — they're valid for 1 hour, cache for 50 min to avoid expiry edge cases
const _urlCache = new Map();
const URL_CACHE_TTL = 50 * 60 * 1000;

export async function getDocumentUrl(storagePath) {
  if (!storagePath || !supabase) return null;
  const safePath = await assertReadableDocumentStoragePath(storagePath);
  const cached = _urlCache.get(safePath);
  if (cached && Date.now() - cached.ts < URL_CACHE_TTL) return cached.url;
  const { data } = await supabase.storage
    .from('documents')
    .createSignedUrl(safePath, 3600);
  const url = data?.signedUrl ?? null;
  if (url) _urlCache.set(safePath, { url, ts: Date.now() });
  return url;
}

// ── USERS / PROFILES ──────────────────────────────────────────────────────────

async function getUsers() {
  const rows = await fetchAllRows('getUsers', () => supabase
    .from('profiles').select('*').order('id', { ascending: true }));
  return rows.map(dbUserToApp);
}

async function addUser(user) {
  const { error } = await supabase.from('profiles').insert(appUserToDb(user));
  if (error) handleError('addUser', error);
}

async function updateUser(id, updates) {
  const { error } = await supabase.from('profiles').update(appUserToDb(updates)).eq('id', id);
  if (error) handleError('updateUser', error);
}

async function deleteUser(id) {
  const { error } = await supabase.auth.admin.deleteUser(id);
  if (error) handleError('deleteUser', error);
}

function appUserToDb(u) {
  const d = {};
  if (u.email       !== undefined) d.email       = u.email;
  if (u.name        !== undefined) d.name         = u.name;
  if (u.role        !== undefined) d.role         = u.role;
  if (u.phone       !== undefined) d.phone        = u.phone;
  if (u.avatarUrl   !== undefined) d.avatar_url   = u.avatarUrl;
  if (u.jobTitle    !== undefined) d.job_title    = u.jobTitle;
  if (u.position    !== undefined) d.position     = u.position;
  if (u.commissionRate !== undefined) d.commission_rate = u.commissionRate;
  if (u.payoutCompany !== undefined) d.payout_company = u.payoutCompany;
  if (u.permissions !== undefined) d.permissions  = u.permissions;
  if (u.companyId   !== undefined) d.company_id   = u.companyId;
  return d;
}

// ── ASSIGNMENTS ───────────────────────────────────────────────────────────────

async function getAssignments() {
  const rows = await fetchAllRows('getAssignments', () => supabase
    .from('assignments').select('*')
    .order('created_at', { ascending: false }).order('id', { ascending: false }));
  return rows.map(dbAssignmentToApp);
}

export function dbAssignmentToApp(a) {
  return {
    id: a.id, title: a.title, projectName: a.project_name,
    clientId: a.client_id, clientName: a.client_name,
    clientEmail: a.client_email, clientPhone: a.client_phone,
    county: a.county, state: a.state, description: a.description,
    quarterCall: a.quarter_call, section: a.section, township: a.township, range: a.range,
    abstractNo: a.abstract_no, oilGasLease: a.oil_gas_lease,
    legalDescription: a.legal_description, effectiveDate: a.effective_date,
    depthLimitations: a.depth_limitations, notes: a.notes,
    status: a.status, priority: a.priority,
    requestType: a.request_type, assignedTo: a.assigned_to,
    assignedToName: a.assigned_to_name, flowchartId: a.flowchart_id,
    runsheetId: a.runsheet_id, dueDate: a.due_date,
    servicePackage: a.service_package, quoteAmount: a.quote_amount,
    paymentStatus: a.payment_status,
    wellInfo: a.well_info,
    courthouseUrl: a.courthouse_url,
    tracts: a.tracts || [],
    acreage: a.acreage,
    buyPriceNRA: a.buy_price_nra,
    sellPriceNRA: a.sell_price_nra,
    createdBy: a.created_by,
    createdAt: a.created_at, updatedAt: a.updated_at,
  };
}
// ── FLOWCHARTS ────────────────────────────────────────────────────────────────

export function dbFlowchartToApp(r) {
  return {
    id: r.id,
    // assignmentId kept during the transition so backfill-lagging rows
    // still render. Drops in Phase D after migration-020.
    assignmentId: r.assignment_id,
    dealId: r.deal_id || null,
    interestEstimateId: r.interest_estimate_id || null,
    title: r.title,
    nodes: r.nodes || [], edges: r.edges || [],
    createdAt: r.created_at, updatedAt: r.updated_at,
  };
}

async function getFlowcharts() {
  const rows = await fetchAllRows('getFlowcharts', () => supabase
    .from('flowcharts').select('*').order('id', { ascending: true }));
  return rows.map(dbFlowchartToApp);
}

// Same missing-column fallback pattern as documents. Tracks which
// post-migration columns the DB is missing and strips them from
// subsequent inserts/updates.
const _missingFlowchartColumns = new Set();
const _missingOwnershipColumns = new Set();
function noteMissingColumn(set, tableName, error, migrationName = 'the latest Supabase migration') {
  if (!error) return false;
  const m = (error.message || '').match(new RegExp(`column\\s+(?:${tableName}\\.)?(\\w+)\\s+does not exist`, 'i'));
  if (!m) return false;
  const col = m[1];
  if (set.has(col)) return false;
  set.add(col);
  console.warn(`[supabaseStore] ${tableName}.${col} column is missing — dropping it from future queries. Run ${migrationName} to add it.`);
  return true;
}
function pruneByMissing(set, row) {
  if (set.size === 0) return row;
  const out = { ...row };
  for (const col of set) delete out[col];
  return out;
}

async function addFlowchart(f) {
  const uid = await currentUserId();
  const row = {
    id: f.id,
    assignment_id: f.assignmentId || null,
    deal_id: f.dealId || null,
    interest_estimate_id: f.interestEstimateId || null,
    title: f.title || 'Title Chain',
    nodes: f.nodes || [], edges: f.edges || [], created_by: uid,
  };
  for (let attempt = 0; attempt < 4; attempt++) {
    const { error } = await supabase.from('flowcharts').insert(pruneByMissing(_missingFlowchartColumns, row));
    if (!error) return;
    if (!noteMissingColumn(_missingFlowchartColumns, 'flowcharts', error)) {
      handleError('addFlowchart', error);
      return;
    }
  }
  handleError('addFlowchart', new Error('Too many missing-column retries on addFlowchart'));
}

async function updateFlowchart(id, updates) {
  const patch = {};
  if (updates.title !== undefined) patch.title = updates.title;
  if (updates.nodes !== undefined) patch.nodes = updates.nodes;
  if (updates.edges !== undefined) patch.edges = updates.edges;
  if (updates.dealId !== undefined) patch.deal_id = updates.dealId;
  if (updates.interestEstimateId !== undefined) patch.interest_estimate_id = updates.interestEstimateId;
  for (let attempt = 0; attempt < 4; attempt++) {
    const { error } = await supabase.from('flowcharts').update(pruneByMissing(_missingFlowchartColumns, patch)).eq('id', id);
    if (!error) return;
    if (!noteMissingColumn(_missingFlowchartColumns, 'flowcharts', error)) {
      handleError('updateFlowchart', error);
      return;
    }
  }
  handleError('updateFlowchart', new Error('Too many missing-column retries on updateFlowchart'));
}

async function deleteFlowchart(id) {
  const { error } = await supabase.from('flowcharts').delete().eq('id', id);
  if (error) handleError('deleteFlowchart', error);
}

// ── RUNSHEETS ─────────────────────────────────────────────────────────────────

// ── DOCUMENTS ─────────────────────────────────────────────────────────────────

// Base column list. Some installs haven't run migration-019 yet, so the
// new deal_id / interest_estimate_id columns may not exist. We lazily
// record any column the DB rejects and omit it from subsequent queries
// + inserts so the app keeps working against older schemas.
const DOC_COLUMNS_ALL = [
  'id', 'assignment_id', 'deal_id', 'interest_estimate_id',
  'name', 'doc_type', 'instrument_title', 'storage_path',
  'file_size', 'source', 'uploaded_at', 'grantor', 'grantee', 'effective_date',
  'file_date', 'volume', 'page', 'instrument_no', 'legal_description',
  'original_filename', 'grantor_address', 'grantee_address',
  'state', 'county',
  // Runsheet (migration-029) — added to the SELECT list so the
  // library reads them on every query. Pre-migration installs end up
  // in _missingDocColumns and the prune retry kicks the column off.
  'section', 'township', 'range', 'consideration', 'runsheet_notes',
];
const _missingDocColumns = new Set();
function activeDocColumns() {
  return DOC_COLUMNS_ALL.filter((c) => !_missingDocColumns.has(c)).join(',');
}
function pruneMissingDocFields(row) {
  if (_missingDocColumns.size === 0) return row;
  const out = { ...row };
  for (const col of _missingDocColumns) delete out[col];
  return out;
}
// Parse PostgREST's "column X does not exist" error and record it.
// Returns true if a new missing column was recorded (so the caller can
// retry), false otherwise.
function noteMissingDocColumnFromError(error) {
  if (!error) return false;
  const message = error.message || '';
  const m = message.match(/column\s+(?:documents\.)?(\w+)\s+does not exist/i)
    || message.match(/'(\w+)'\s+column\s+of\s+'documents'/i);
  if (!m) return false;
  const col = m[1];
  if (!DOC_COLUMNS_ALL.includes(col)) return false;
  if (_missingDocColumns.has(col)) return false;
  _missingDocColumns.add(col);
  console.warn(`[supabaseStore] documents.${col} column is missing — dropping it from future document queries/writes until the matching migration is run.`);
  return true;
}

/**
 * Fetch recent documents (default 50). Used for initial page load.
 */
async function getDocuments(limit = 50) {
  for (let attempt = 0; attempt < 4; attempt++) {
    const { data, error } = await supabase
      .from('documents').select(activeDocColumns()).order('uploaded_at', { ascending: false })
      .range(0, limit - 1);
    if (!error) return (data || []).map(dbDocToApp);
    if (!noteMissingDocColumnFromError(error)) {
      handleError('getDocuments', error);
      return [];
    }
  }
  // Ran out of retries — bail with the latest error shape.
  handleError('getDocuments', new Error('Too many missing-column retries'));
  return [];
}

/**
 * Fetch every document belonging to one deal — by deal_id plus any ids
 * the deal has attached. The initial page load only pulls the 50 most
 * recent documents, so a deal whose paperwork is older than that would
 * otherwise look empty to the checklist evidence checks.
 */
async function getDocumentsForDeal(dealId, attachedIds = []) {
  const ids = (Array.isArray(attachedIds) ? attachedIds : []).filter(Boolean).map(String);
  if (!dealId && !ids.length) return [];
  const byId = new Map();
  const pull = async (build) => {
    const { data, error } = await build(supabase.from('documents').select(activeDocColumns()));
    if (error) {
      handleError('getDocumentsForDeal', error);
      return;
    }
    (data || []).forEach((row) => byId.set(row.id, row));
  };
  if (dealId) await pull((q) => q.eq('deal_id', dealId));
  for (let i = 0; i < ids.length; i += 100) {
    await pull((q) => q.in('id', ids.slice(i, i + 100)));
  }
  return Array.from(byId.values()).map(dbDocToApp);
}

/**
 * Search documents server-side using Supabase ilike on multiple columns.
 * Paginates to return ALL matching documents.
 */
function normalizeDocumentScope(scope = {}) {
  const state = String(scope.state || '').trim().toUpperCase();
  const county = String(scope.county || '').trim();
  return {
    state: /^[A-Z]{2}$/.test(state) ? state : '',
    county,
  };
}

function applyDocumentScopeFilters(query, scope = {}) {
  const { state, county } = normalizeDocumentScope(scope);
  let scoped = query;
  if (state && !_missingDocColumns.has('state')) scoped = scoped.eq('state', state);
  if (county && !_missingDocColumns.has('county')) scoped = scoped.ilike('county', `%${county}%`);
  return scoped;
}

async function searchDocuments(query, scope = {}) {
  if (!query || query.trim().length < 2) return [];
  const trimmed = query.trim();
  const q = `%${trimmed}%`;
  const PAGE = 1000;
  let all = [];
  let from = 0;
  let useFts = true;
  const ilikeFilter = `name.ilike.${q},grantor.ilike.${q},grantee.ilike.${q},volume.ilike.${q},page.ilike.${q},instrument_no.ilike.${q},instrument_title.ilike.${q},legal_description.ilike.${q},original_filename.ilike.${q}`;

  // Prefer the GIN-backed `search_vec` column from migration 015. Falls back
  // to OR-chained ilike if the column doesn't exist yet (older DBs) or if
  // the FTS query hits a parser error.
  async function runPage() {
    for (let attempt = 0; attempt < 4; attempt++) {
      let res;
      if (useFts) {
        res = await applyDocumentScopeFilters(supabase
          .from('documents').select(activeDocColumns())
          .textSearch('search_vec', trimmed, { type: 'websearch', config: 'english' }), scope)
          .order('uploaded_at', { ascending: false })
          .range(from, from + PAGE - 1);
        if (res.error) {
          if (noteMissingDocColumnFromError(res.error)) continue;
          useFts = false;
          continue;
        }
        return res;
      }
      res = await applyDocumentScopeFilters(supabase
        .from('documents').select(activeDocColumns())
        .or(ilikeFilter), scope)
        .order('uploaded_at', { ascending: false })
        .range(from, from + PAGE - 1);
      if (!res.error) return res;
      if (!noteMissingDocColumnFromError(res.error)) return res;
    }
    return { data: null, error: new Error('Too many missing-column retries') };
  }

  while (true) {
    const { data, error } = await runPage();
    if (error) handleError('searchDocuments', error);
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < PAGE) break;
    from += PAGE;
  }

  return all.map(dbDocToApp);
}

/**
 * Fetch ALL documents (paginated). Used only for operations that need the full set
 * (e.g. duplicate detection during bulk upload).
 */
async function getAllDocuments() {
  const PAGE = 1000;
  let all = [];
  let from = 0;

  while (true) {
    let data = null;
    let error = null;
    for (let attempt = 0; attempt < 4; attempt++) {
      const res = await supabase
        .from('documents').select(activeDocColumns()).order('uploaded_at', { ascending: false })
        .range(from, from + PAGE - 1);
      if (!res.error) { data = res.data; error = null; break; }
      if (!noteMissingDocColumnFromError(res.error)) { error = res.error; break; }
    }
    if (error) handleError('getAllDocuments', error);
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < PAGE) break;
    from += PAGE;
  }

  return all.map(dbDocToApp);
}

/**
 * Get total document count (lightweight — no row data fetched).
 */
async function getDocumentCount() {
  const { count, error } = await supabase
    .from('documents').select('id', { count: 'exact', head: true });
  if (error) { console.warn('[supabaseStore] getDocumentCount error:', error.message); return 0; }
  return count || 0;
}

async function addDocument(doc) {
  const uid = await currentUserId();
  let storagePath = doc.storagePath ?? null;
  if (storagePath) storagePath = await assertTenantStoragePath(storagePath);
  // Preferred path: a raw File/Blob streams straight to storage — no
  // base64 round trip, no multi-MB string on the main thread.
  if (!storagePath && doc.file instanceof Blob) {
    storagePath = await uploadFileDirectly(doc.id, doc.file);
  }
  const hasFile = !!(doc.dataUrl || doc.fileDataUrl);
  if (!storagePath && hasFile) {
    // uploadDocumentFile now throws on storage failure — let the error
    // propagate so we don't insert an orphan metadata row that can't
    // be viewed later.
    storagePath = await uploadDocumentFile(doc.id, doc.dataUrl || doc.fileDataUrl, doc.name);
    if (!storagePath) {
      throw new Error(`Storage upload failed for "${doc.name}" — check Supabase bucket permissions.`);
    }
  }
  if (!hasFile && !storagePath && !(doc.file instanceof Blob)) {
    console.warn('[supabaseStore] addDocument: no file data and no storagePath. docId:', doc.id);
  }
  const row = {
    id: doc.id,
    assignment_id: doc.assignmentId || null,
    deal_id: doc.dealId || null,
    interest_estimate_id: doc.interestEstimateId || null,
    name: documentDisplayName(doc),
    doc_type: doc.docType, instrument_title: doc.instrumentTitle,
    storage_path: storagePath, file_size: typeof doc.size === 'number' ? doc.size : null, source: doc.source,
    uploaded_at: doc.uploadedAt || new Date().toISOString(), uploaded_by: uid,
    grantor: doc.grantor, grantee: doc.grantee,
    grantor_address: doc.grantorAddress || null,
    grantee_address: doc.granteeAddress || null,
    effective_date: sanitizeDate(doc.effectiveDate), file_date: sanitizeDate(doc.fileDate),
    volume: doc.volume, page: doc.page, instrument_no: doc.instrumentNo,
    legal_description: doc.legalDescription, original_filename: doc.originalFilename || null,
    state: doc.state || null,
    county: doc.county || null,
    // Runsheet fields (migration-029). Pre-migration installs lack
    // these columns; the retry loop below drops any unknown column
    // and tries again so this row still inserts.
    section: doc.section || null,
    township: doc.township || null,
    range: doc.range || null,
    consideration: doc.consideration || null,
    runsheet_notes: doc.runsheetNotes || null,
  };
  // Retry loop: drop any column PostgREST complains about, then try again.
  // Covers older DBs missing original_filename (pre-migration-014) and/or
  // deal_id / interest_estimate_id (pre-migration-019).
  for (let attempt = 0; attempt < 4; attempt++) {
    const { error } = await supabase.from('documents').insert(pruneMissingDocFields(row));
    if (!error) {
      await recordAuditEventQuietly({
        action: 'document.created',
        entityType: 'document',
        entityId: doc.id,
        dealId: doc.dealId || null,
        afterData: {
          name: documentDisplayName(doc),
          docType: doc.docType,
          storagePath,
          state: doc.state || null,
          county: doc.county || null,
        },
      });
      // Callers (AppContext) use the storagePath to swap their
      // optimistic state row to a storage-backed one and drop the
      // multi-MB dataUrl string from memory.
      return { storagePath };
    }
    if (!noteMissingDocColumnFromError(error)) {
      handleError('addDocument', error);
      return { storagePath };
    }
  }
  handleError('addDocument', new Error('Too many missing-column retries on addDocument'));
  return { storagePath };
}

async function updateDocument(id, updates) {
  const map = {
    name: 'name', docType: 'doc_type', instrumentTitle: 'instrument_title',
    source: 'source', grantor: 'grantor', grantee: 'grantee',
    grantorAddress: 'grantor_address', granteeAddress: 'grantee_address',
    effectiveDate: 'effective_date', fileDate: 'file_date',
    volume: 'volume', page: 'page', instrumentNo: 'instrument_no',
    legalDescription: 'legal_description',
    dealId: 'deal_id', interestEstimateId: 'interest_estimate_id',
    state: 'state', county: 'county',
    // Runsheet fields (migration-029).
    section: 'section', township: 'township', range: 'range',
    consideration: 'consideration', runsheetNotes: 'runsheet_notes',
  };
  const patch = Object.fromEntries(
    Object.entries(map).filter(([k]) => updates[k] !== undefined).map(([k, v]) => [v, updates[k]])
  );
  for (let attempt = 0; attempt < 4; attempt++) {
    const { error } = await supabase.from('documents').update(pruneMissingDocFields(patch)).eq('id', id);
    if (!error) return;
    if (!noteMissingDocColumnFromError(error)) {
      handleError('updateDocument', error);
      return;
    }
  }
  handleError('updateDocument', new Error('Too many missing-column retries on updateDocument'));
}

async function deleteDocument(id) {
  // Also delete the file from storage if it exists. Use .maybeSingle()
  // so a missing row returns null instead of raising PostgREST's 406
  // "no rows" error — deleteDocument is sometimes called defensively
  // (e.g. orphan cleanup after a failed upload) where the row may
  // legitimately not exist.
  const { data: doc } = await supabase.from('documents').select('storage_path').eq('id', id).maybeSingle();
  if (doc?.storage_path) {
    const safePath = await assertTenantStoragePath(doc.storage_path);
    const { error: storageErr } = await supabase.storage.from('documents').remove([safePath]);
    if (storageErr) console.warn('[supabaseStore] Failed to delete storage file:', storageErr.message);
  }
  const { error } = await supabase.from('documents').delete().eq('id', id);
  if (error) handleError('deleteDocument', error);
  await recordAuditEventQuietly({
    action: 'document.deleted',
    entityType: 'document',
    entityId: id,
    beforeData: doc || null,
  });
}

// ── CONTACTS ──────────────────────────────────────────────────────────────────

// Exactly what dbContactToApp reads. The three it leaves out —
// created_by, updated_at, tenant_id — are mapped nowhere and were going
// to every browser for nothing.
//
// Worth saying what this is and is not worth, because the uncompressed
// figure flatters it: all 21 columns of 1,856 contacts are 1,080 kB of
// JSON, but it is mostly the same key names repeated 1,856 times, and
// that is the most compressible thing there is. Gzipped it is about
// 66 kB, and dropping these three takes roughly 4 kB off the wire.
// Trimming it to the dozen columns the board actually reads would save
// about 17 kB more and would mean the Lead Center loading contacts
// again when it opens — which is not a trade worth making for 17 kB.
const CONTACT_APP_COLUMNS = [
  'id', 'type', 'first_name', 'last_name', 'full_name',
  'company_id', 'company_name', 'title', 'name', 'entity_type',
  'email', 'phone', 'address', 'city', 'state', 'zip', 'notes', 'created_at',
].join(', ');

async function getContacts() {
  const rows = await fetchAllRows('getContacts', () => supabase
    .from('contacts').select(CONTACT_APP_COLUMNS)
    .order('created_at', { ascending: false }).order('id', { ascending: false }));
  return rows.map(dbContactToApp);
}

async function addContact(c) {
  const uid = await currentUserId();
  // Force a round-trip so we fail loudly on RLS / validation errors and
  // the caller can rely on the contact actually existing before it
  // references the id elsewhere (e.g. as deal.contact_id).
  const { data, error } = await supabase
    .from('contacts')
    .insert({ ...appContactToDb(c), created_by: uid })
    .select()
    .single();
  if (error) handleError('addContact', error);
  return data ? dbContactToApp(data) : null;
}

async function updateContact(id, updates) {
  const { error } = await supabase.from('contacts').update(appContactToDb(updates)).eq('id', id);
  if (error) handleError('updateContact', error);
}

async function deleteContact(id) {
  const { error } = await supabase.from('contacts').delete().eq('id', id);
  if (error) handleError('deleteContact', error);
}

function appContactToDb(c) {
  const map = {
    id: 'id', type: 'type', firstName: 'first_name', lastName: 'last_name',
    fullName: 'full_name', companyId: 'company_id', companyName: 'company_name',
    title: 'title', name: 'name', entityType: 'entity_type',
    email: 'email', phone: 'phone', address: 'address',
    city: 'city', state: 'state', zip: 'zip', notes: 'notes',
  };
  return Object.fromEntries(
    Object.entries(map).filter(([k]) => c[k] !== undefined).map(([k, v]) => [v, c[k]])
  );
}

// ── OWNERSHIP RECORDS ─────────────────────────────────────────────────────────

async function getOwnership() {
  // This one had no ORDER BY at all, so which rows a truncated
  // response kept was up to the planner. Order by id for a stable
  // page walk.
  const rows = await fetchAllRows('getOwnership', () => supabase
    .from('ownership_records').select('*').order('id', { ascending: true }));
  return rows.map(dbOwnershipToApp);
}

async function getOwnershipByAssignment(assignmentId) {
  const { data, error } = await supabase
    .from('ownership_records').select('*').eq('assignment_id', assignmentId).single();
  if (error) return null;
  return dbOwnershipToApp(data);
}

// Post-migration-019 lookup. Fetches the ownership row keyed by the
// (deal_id, interest_estimate_id) pair that replaces assignment_id
// as the ownership discriminator. Returns null if no row exists yet
// so callers can fall back to a no-ownership render.
async function getOwnershipForEstimate(dealId, interestEstimateId) {
  if (!dealId || !interestEstimateId) return null;
  const { data, error } = await supabase
    .from('ownership_records').select('*')
    .eq('deal_id', dealId)
    .eq('interest_estimate_id', interestEstimateId)
    .maybeSingle();
  if (error) return null;
  return data ? dbOwnershipToApp(data) : null;
}

async function saveOwnership(record) {
  const uid = await currentUserId();
  const row = {
    id: record.id,
    assignment_id: record.assignmentId || null,
    deal_id: record.dealId || null,
    interest_estimate_id: record.interestEstimateId || null,
    surface: record.surface || [], mineral: record.mineral || [],
    working_interest: record.workingInterest || [],
    updated_by: uid,
  };
  for (let attempt = 0; attempt < 4; attempt++) {
    const { error } = await supabase.from('ownership_records').upsert(pruneByMissing(_missingOwnershipColumns, row));
    if (!error) return;
    if (!noteMissingColumn(_missingOwnershipColumns, 'ownership_records', error)) {
      handleError('saveOwnership', error);
      return;
    }
  }
  handleError('saveOwnership', new Error('Too many missing-column retries on saveOwnership'));
}

async function deleteOwnership(id) {
  const { error } = await supabase.from('ownership_records').delete().eq('id', id);
  if (error) handleError('deleteOwnership', error);
}

export function dbOwnershipToApp(r) {
  return {
    id: r.id,
    // assignmentId retained through Phase D for backfill-lagging rows.
    assignmentId: r.assignment_id,
    dealId: r.deal_id || null,
    interestEstimateId: r.interest_estimate_id || null,
    surface: r.surface || [], mineral: r.mineral || [],
    workingInterest: r.working_interest || [],
    updatedAt: r.updated_at,
  };
}

// ── DEALS (Owner Pipeline) ───────────────────────────────────────────────────

function appDealToDb(d) {
  const map = {
    ownerName: 'owner_name', county: 'county', state: 'state',
    legalDescription: 'legal_description', acreage: 'acreage', nma: 'nma',
    offerAmount: 'offer_amount', askingPrice: 'asking_price', pricePerNma: 'price_per_nma',
    stage: 'stage', priority: 'priority', notes: 'notes',
    titleNotes: 'title_notes',
    finalTitleNotes: 'final_title_notes',
    closingConditions: 'closing_conditions',
    archived: 'archived',
    assetForSale: 'asset_for_sale',
    assetListedAt: 'asset_listed_at',
    assetSale: 'asset_sale',
    buyerPsa: 'buyer_psa',
    titleAssignment: 'title_assignment',
    parentDealId: 'parent_deal_id',
    nextFollowUp: 'next_follow_up',
    nextFollowUpNote: 'next_follow_up_note',
    passReason: 'pass_reason',
    passNote: 'pass_note',
    passedAt: 'passed_at',
    passedBy: 'passed_by',
    contactId: 'contact_id', crmContactId: 'crm_contact_id',
    effectiveDate: 'effective_date', closingDate: 'closing_date',
    buyClosingDate: 'buy_closing_date', sellClosingDate: 'sell_closing_date',
    buyRateNRA: 'buy_rate_nra', sellRateNRA: 'sell_rate_nra', sellPrice: 'sell_price',
    buySideAgentId: 'buy_side_agent_id', sellSideAgentId: 'sell_side_agent_id',
    coordinatorId: 'coordinator_id',
    coordinatorCommissionPct: 'coordinator_commission_pct',
    dispositionManagerId: 'disposition_manager_id',
    dispositionBuyerId: 'disposition_buyer_id', lifecycle: 'lifecycle',
    investorId: 'investor_id', investorEntityId: 'investor_entity_id',
    investorCapital: 'investor_capital', investorPlacedOn: 'investor_placed_on',
    investorThesis: 'investor_thesis',
    // todos / activities / signatureRequests intentionally OMITTED —
    // they live in deal_todos / deal_activities / deal_signature_requests
    // tables now. updateDeal({todos:[...]}) is a no-op; callers must use
    // store.addDealTodo / store.addDealActivity / store.upsertDealSignatureRequest.
    notifications: 'notifications',
    attachedDocumentIds: 'attached_document_ids',
    tractRates: 'tract_rates',
    tractWIRates: 'tract_wi_rates',
    interestEstimates: 'interest_estimates',
    wiringInfo: 'wiring_info',
    closingDocs: 'closing_docs',
    assignmentIds: 'assignment_ids',
  };
  return Object.fromEntries(
    Object.entries(map)
      .filter(([k]) => d[k] !== undefined)
      .map(([k, v]) => [v, blankToNull(v, d[k])])
  );
}

// Scalar columns + lifecycle that the deals list / kanban / calendar
// actually render. The heavy JSONB columns (activities, notifications,
// todos, attached_document_ids) are only consumed inside OwnerDashboard
// and get pulled on-demand via getDealFull below.
//
// `interest_estimates` IS included in the list query even though it's
// JSONB — the Map page iterates `deals` and reads `interestEstimates`
// to place per-tract markers, so omitting it from the list query
// leaves the map empty. `tract_rates` is also JSONB and already lives
// in the list query for the same reason (Pricing + Deal Tracker cards).
// `todos` joined the list query once the Deal Tracker grew a global
// "All To-Dos" aggregator button — iterating getDealFull per deal
// would N+1 the kanban load, so we pay the JSONB payload cost in the
// list query instead.
// Columns the kanban + Deal Calendar + Accounting + MapPage actually
// read off each deal. Trimmed from the original "everything except
// activities/notifications" set after an audit found:
//
//   legal_description, acreage, nma, price_per_nma, priority, notes,
//   sell_price
//
// were never accessed on any list-consumer code path. They can still
// be read individually via getDealFull(id) which selects '*'. Dropping
// them shrinks every getDeals() payload — `notes` in particular could
// be multi-paragraph free text.
const BASE_DEAL_LIST_COLUMNS = [
  'id', 'owner_name', 'county', 'state',
  'offer_amount', 'asking_price', 'stage',
  'contact_id', 'crm_contact_id', 'effective_date', 'closing_date',
  'buy_closing_date', 'sell_closing_date',
  'buy_rate_nra', 'sell_rate_nra',
  'buy_side_agent_id', 'sell_side_agent_id', 'coordinator_id', 'coordinator_commission_pct',
  'disposition_manager_id', 'disposition_buyer_id',
  // The Deal Tracker marks an investor-placed card, so these ride the
  // list query; without them every card would look unplaced until
  // somebody opened it.
  'investor_id', 'investor_entity_id', 'investor_capital', 'investor_placed_on',
  'lifecycle', 'tract_rates', 'interest_estimates', 'todos', 'signature_requests',
  // `closing_docs` rides the list query so the Deal Tracker card can
  // judge the closing packet off real data instead of guessing — it
  // was calling complete packets "incomplete" because it could not
  // see them. It is stored metadata, not file bytes: ~10 kB across
  // the whole deals table, next to ~300 kB for interest_estimates.
  'closing_docs',
  'archived', 'asset_for_sale', 'asset_sale', 'parent_deal_id',
  'pass_reason', 'pass_note', 'passed_at', 'passed_by',
  // The board flags a card whose follow-up date has arrived, and
  // shows the note on hover. A date and a short line per deal.
  'next_follow_up', 'next_follow_up_note',
  // Small — one receipt per deal — and read on the deal dashboard to
  // decide whether the Title Services button offers to send or reports
  // that it already went.
  'title_assignment',
  'created_by', 'created_at', 'updated_at',
];
// What a Passed deal needs on the Deal Tracker, and no more.
//
// Passed is the single largest stage — 122 of 220 deals — and its card
// collapses to a drag strip and the owner's name. It is also hidden
// behind a toggle that defaults to off. Yet it was carrying the same
// payload as a live deal: 241 kB of interest_estimates alone, 63% of
// everything the board downloads, parsed and rolled up on every load
// so the result could be thrown away.
//
// The heavy JSON is what gets dropped — the estimator rows, tract
// rates, closing packet and signature receipts. None of them are read
// for a Passed card, none feed a KPI (every KPI filters Passed out
// first), and opening one loads the deal in full through getDealFull
// anyway, so dragging a deal back out of Passed still has everything.
const PASSED_DEAL_OMITTED_COLUMNS = new Set([
  'interest_estimates', 'tract_rates', 'closing_docs', 'signature_requests', 'todos',
]);

// Columns we'll drop from the list query once we know a given DB
// doesn't have them. This avoids the noisy 400 that Chrome logs every
// time we retry the full column set on a stale DB. Populated lazily
// the first time getDeals() sees a "column does not exist" error.
const _missingDealColumns = new Set();
function activeDealListColumns({ slim = false } = {}) {
  return BASE_DEAL_LIST_COLUMNS
    .filter((c) => !_missingDealColumns.has(c))
    .filter((c) => !(slim && PASSED_DEAL_OMITTED_COLUMNS.has(c)))
    .join(', ');
}

/** The Passed half on its own, for loading after the board has painted. */
async function getPassedDeals() {
  const rows = await fetchAllRows('getDeals:passed', () => supabase
    .from('deals').select(activeDealListColumns({ slim: true }))
    .eq('stage', 'Passed')
    .order('updated_at', { ascending: false }).order('id', { ascending: false }));
  return rows.map(dbDealToApp);
}

async function getDeals({ liveOnly = false } = {}) {
  // updated_at + id gives the total order fetchAllRows needs; deals
  // touched in the same transaction share updated_at.
  const dealPage = (columns, shape = 'all') => () => {
    let query = supabase.from('deals').select(columns);
    // Split so the dead half of the board can travel light. 'all' is
    // kept for the wildcard fallback below, where the point is to get
    // the query working at any cost rather than to save bytes.
    if (shape === 'live') query = query.neq('stage', 'Passed');
    if (shape === 'passed') query = query.eq('stage', 'Passed');
    return query
      .order('updated_at', { ascending: false }).order('id', { ascending: false });
  };

  // Both halves, mapped through the same row mapper. A Passed row simply
  // arrives without the heavy JSON, which reads as absent rather than
  // empty — the card shows the same single line either way.
  //
  // `liveOnly` leaves the Passed half for getPassedDeals(), so the board
  // can paint without waiting on it. The two halves are 923 kB and 312
  // kB, and nothing on the board's first paint reads a Passed deal:
  // every KPI filters Passed out, the to-do list skips it, and the
  // column is one line per card. Passed still loads, a moment later —
  // the slim list keeps `lifecycle` precisely because dragging a deal
  // back out of Passed rebuilds its checklist from it, so this moves
  // when it arrives and not what arrives.
  const bothHalves = async ({ liveOnly = false } = {}) => {
    // Skipped, not fetched-and-discarded.
    if (liveOnly) {
      const liveRows = await fetchAllRows('getDeals', dealPage(activeDealListColumns(), 'live'));
      return liveRows.map(dbDealToApp);
    }
    // And when both are wanted they still go out together: a caller
    // asking for the whole board should not wait for one query and then
    // the other.
    const [live, passed] = await Promise.all([
      fetchAllRows('getDeals', dealPage(activeDealListColumns(), 'live')),
      fetchAllRows('getDeals:passed', dealPage(activeDealListColumns({ slim: true }), 'passed')),
    ]);
    return [...live, ...passed].map(dbDealToApp);
  };

  // Probe one row first so the missing-column fallbacks below still
  // work — fetchAllRows treats a query error as fatal.
  const probe = await dealPage(activeDealListColumns())().range(0, 0);
  if (probe.error) {
    if (/column .* does not exist/i.test(probe.error.message || '')) {
      // Parse the missing column name out of the PostgreSQL message
      // so the next query can skip it. Falls back to '*' if we can't
      // figure out which column tripped.
      const m = (probe.error.message || '').match(/column\s+(?:deals\.)?(\w+)\s+does not exist/i);
      if (m && m[1]) {
        _missingDealColumns.add(m[1]);
        // Retry with the pruned list before falling back to wildcard.
        const retry = await dealPage(activeDealListColumns())().range(0, 0);
        if (!retry.error) return bothHalves({ liveOnly });
      }
      const rows = await fetchAllRows('getDeals', dealPage('*'));
      return rows.map(dbDealToApp);
    }
    handleError('getDeals', probe.error);
    return [];
  }

  return bothHalves({ liveOnly });
}

async function getDealFull(id) {
  if (!id) return null;
  const { data, error } = await supabase.from('deals').select('*').eq('id', id).maybeSingle();
  if (error) handleError('getDealFull', error);
  return data ? dbDealToApp(data) : null;
}

// ── Normalized children of deals (migration-028) ────────────────────────────
// todos, activities, signature_requests now live in their own tables so
// concurrent writes can't lose data. The mapper converts snake_case
// columns back to the camelCase shape the UI expects.

function dbActivityToApp(r) {
  return {
    id: r.id,
    dealId: r.deal_id,
    type: r.type,
    text: r.text,
    actorId: r.actor_id,
    actorName: r.actor_name,
    createdAt: r.created_at,
    archived: !!r.archived,
    ...(r.meta && typeof r.meta === 'object' ? r.meta : {}),
  };
}

async function getDealTodos() {
  const rows = await fetchAllRows('getDealTodos', () => supabase
    .from('deal_todos').select('*')
    .order('created_at', { ascending: false }).order('id', { ascending: false }));
  return rows.map(dbTodoToApp);
}

async function getDealActivities() {
  const rows = await fetchAllRows('getDealActivities', () => supabase
    .from('deal_activities').select('*')
    .order('created_at', { ascending: false }).order('id', { ascending: false }));
  return rows.map(dbActivityToApp);
}

/**
 * The board does not draw a single activity row — it only asks how long
 * a deal has been quiet, which is one timestamp per deal. Fetching every
 * row to answer that moved 963 kB (2,563 rows on 2026-10-21) on every
 * page load, for data nothing on screen could show.
 *
 * So the board gets stamps and the deal page gets rows
 * (getDealActivitiesForDeal, below, called when a deal opens).
 *
 * Two columns rather than an aggregate: PostgREST can group server-side,
 * but a `select=deal_id,created_at.max()` is a syntax this project has
 * never used and would fail silently into an empty board if the server
 * did not take it. Two columns is certain, and ~85% less either way. If
 * the table grows another order of magnitude, an RPC returning one row
 * per deal is the next move.
 */
async function getDealActivityStamps() {
  // created_at + id, because created_at alone is not a total order:
  // activities written in the same transaction share it, and a page walk
  // over a non-total order can repeat one row and lose another. Losing
  // one here means losing a deal's latest stamp, which quietly puts a
  // staleness warning back on a deal somebody just touched. `id` is
  // ordered on without being selected — the caller only needs the two
  // columns, and the sort does not have to be in the payload.
  const rows = await fetchAllRows('getDealActivityStamps', () => supabase
    .from('deal_activities').select('deal_id, created_at')
    .order('created_at', { ascending: false }).order('id', { ascending: false }));
  // Ordered newest first, so the first row seen for a deal is its latest.
  const latest = new Map();
  for (const row of rows || []) {
    const dealId = row?.deal_id;
    if (!dealId || latest.has(dealId)) continue;
    latest.set(dealId, row.created_at || null);
  }
  return Array.from(latest, ([dealId, lastAt]) => ({ dealId, lastAt }));
}

/**
 * The stored state of every deal the caller can see — what the sweep
 * (api/cron/deal-state-sweep.js) wrote to deal_state_summaries. One
 * small row per deal; the board reads next action and blockers off it
 * instead of judging the card with its own rules. RLS scopes the rows
 * to the deals the caller can read.
 */
async function getDealStateSummaries() {
  const rows = await fetchAllRows('getDealStateSummaries', () => supabase
    .from('deal_state_summaries').select('deal_id, summary, fingerprint, computed_at, source_updated_at')
    .order('deal_id', { ascending: true }));
  return (rows || []).map(dbDealStateSummaryToApp);
}

/** Every activity row for ONE deal — what the deal page reads. */
async function getDealActivitiesForDeal(dealId) {
  if (!dealId) return [];
  const { data, error } = await supabase
    .from('deal_activities').select('*').eq('deal_id', dealId)
    .order('created_at', { ascending: false }).order('id', { ascending: false });
  if (error) {
    handleError('getDealActivitiesForDeal', error);
    return [];
  }
  return (data || []).map(dbActivityToApp);
}

/**
 * Upload one chat attachment and hand back what the message needs.
 *
 * Straight to storage from the browser, not through the API route.
 * Three reasons, and the first is the one that decides it:
 *
 *   · A Vercel function body caps out around 4.5 MB, so a 25 MB plat
 *     could not pass through one at all — and base64 would inflate it
 *     by a third on the way.
 *   · The bucket's own insert policy already says exactly the right
 *     thing: tenants/<your tenant>/... and nothing else
 *     (migration-259). Uploading through the service role would bypass
 *     the check rather than use it.
 *   · The path is built by chatAttachmentPath, which the ROUTE then
 *     rebuilds and compares against. The browser cannot name a path
 *     outside its own tenant and deal and have it accepted.
 *
 * upsert is false: two pastes of the same screenshot in one send would
 * otherwise have the second overwrite the first, and the attachment row
 * of the first would point at the second's bytes.
 */
export async function uploadChatAttachment({ dealId, groupId, file }) {
  if (!supabase) return { ok: false, reason: 'Storage is not configured.' };
  if (!dealId || !groupId || !file) return { ok: false, reason: 'Nothing to upload.' };
  const mime = String(file.type || '').toLowerCase() || 'application/octet-stream';
  if (!ALLOWED_ATTACHMENT_MIME.has(mime)) {
    return { ok: false, reason: `${file.name || 'That file'} is not a type we can attach.` };
  }
  const size = Number(file.size) || 0;
  if (size <= 0) return { ok: false, reason: `${file.name || 'That file'} is empty.` };
  if (size > MAX_ATTACHMENT_BYTES) {
    return {
      ok: false,
      reason: `${file.name || 'That file'} is ${(size / 1024 / 1024).toFixed(1)} MB. The limit is 25 MB.`,
    };
  }
  const tenantId = await currentTenantId();
  if (!tenantId) {
    return { ok: false, reason: 'Your profile is missing a tenant id. Ask an admin to refresh it.' };
  }
  // A paste has no filename worth using, so it gets one that says when.
  const name = String(file.name || '').trim() || pastedAttachmentName(mime);
  const storagePath = chatAttachmentPath({ tenantId, dealId, groupId, name });
  if (!storagePath) return { ok: false, reason: 'That file name could not be used.' };
  const { error } = await supabase.storage
    .from('documents')
    .upload(storagePath, file, { upsert: false, contentType: mime });
  if (error) {
    const status = error.status ?? error.statusCode ?? null;
    console.error('[supabaseStore] chat attachment upload failed', {
      storagePath, mime, size, status, message: error.message,
    });
    if (!status && /failed to fetch/i.test(error.message || '')) {
      return { ok: false, reason: 'The upload did not reach Supabase (VPN, proxy or an extension).' };
    }
    return { ok: false, reason: `Upload failed${status ? ` (HTTP ${status})` : ''}: ${error.message}` };
  }
  return {
    ok: true,
    value: {
      storagePath,
      name: safeAttachmentName(name),
      mime,
      size: Math.round(size),
    },
  };
}

/**
 * A viewable link for one chat attachment.
 *
 * Minted in the browser, which works because the path is under
 * tenants/<tenant>/ and the bucket's restrictive select policy admits
 * exactly that. Routed through the same cache getDocumentUrl uses, so
 * scrolling a thread of twenty screenshots does not mint twenty URLs a
 * second.
 */
export async function getChatAttachmentUrl(storagePath) {
  if (!storagePath || !supabase) return null;
  // The same tenant-prefix assertion documents get. An attachment path
  // always starts tenants/<tenant>/, so this is the strict check and
  // not the legacy-tolerant one.
  const safePath = await assertTenantStoragePath(storagePath);
  if (!safePath) return null;
  const cached = _urlCache.get(safePath);
  if (cached && Date.now() - cached.ts < URL_CACHE_TTL) return cached.url;
  const { data, error } = await supabase.storage
    .from('documents')
    .createSignedUrl(safePath, 3600);
  if (error) {
    console.warn('[supabaseStore] chat attachment url failed:', error.message);
    return null;
  }
  const url = data?.signedUrl ?? null;
  if (url) _urlCache.set(safePath, { url, ts: Date.now() });
  return url;
}

/**
 * Who has acknowledged which note on this deal.
 *
 * The second half of air-1792684688753-debef8. One query for the whole
 * deal rather than one per note — the feed shows every entry at once,
 * so per-row fetching would be one round trip per line on a deal with
 * hundreds.
 *
 * The acknowledger's NAME is not on the acks table; profiles holds it.
 * Joined here through the FK rather than denormalised onto every ack
 * row, because a name that is copied goes stale the day somebody is
 * renamed, and the tick's whole job is saying who.
 *
 * Returns rows, not a Map: grouping is groupAcksByActivity's job in
 * utils/activityAcks.js, where it is tested without a database.
 */
export async function getDealActivityAcks(dealId) {
  if (!dealId) return [];
  const { data, error } = await supabase
    .from('deal_activity_acks')
    .select('activity_id, profile_id, created_at, profiles(name, email)')
    .eq('deal_id', dealId);
  if (error) {
    // The ticks are worth having and the feed is worth more. Until
    // migration-258 is applied this table does not exist, and
    // handleError THROWS — which would take the whole activity log
    // down rather than render it without its ticks. So the missing
    // table is a warning and an empty list, the same way
    // getCertifiedMailJobs handles its own.
    if (isMissingTableError(error, 'deal_activity_acks')) {
      console.warn('[supabaseStore] deal_activity_acks is missing; run supabase/migration-258-deal-activity-acknowledgements.sql to enable acknowledging notes.');
      return [];
    }
    handleError('getDealActivityAcks', error);
  }
  return (data || []).map((row) => ({
    activity_id: row.activity_id,
    profile_id: row.profile_id,
    created_at: row.created_at,
    name: row.profiles?.name || row.profiles?.email || '',
  }));
}

/**
 * Acknowledge a note, or take it back.
 *
 * profile_id is this session's own id and is never passed in. The
 * restrictive own-row policies in migration-258 enforce the same thing
 * in the database, so this is the convenient half of the rule rather
 * than the load-bearing half — but a caller cannot even express
 * acknowledging as somebody else.
 *
 * Upserted on the primary key (activity_id, profile_id), so a double
 * click is one row and cannot inflate a count.
 */
export async function addDealActivityAck(activityId, dealId) {
  if (!activityId || !dealId) return { ok: false, reason: 'activityId and dealId are required' };
  const [uid, tenantId] = await Promise.all([currentUserId(), currentTenantId()]);
  if (!uid) return { ok: false, reason: 'Not signed in' };
  if (!tenantId) return { ok: false, reason: 'Your profile is missing a tenant id' };
  const { error } = await supabase
    .from('deal_activity_acks')
    .upsert({ activity_id: activityId, deal_id: dealId, profile_id: uid, tenant_id: tenantId },
      { onConflict: 'activity_id,profile_id' });
  if (error) {
    if (isMissingTableError(error, 'deal_activity_acks')) {
      return { ok: false, reason: 'Acknowledgements are not enabled yet — migration-258 has not been applied.' };
    }
    handleError('addDealActivityAck', error);
  }
  return { ok: true };
}

export async function removeDealActivityAck(activityId) {
  if (!activityId) return { ok: false, reason: 'activityId is required' };
  const uid = await currentUserId();
  if (!uid) return { ok: false, reason: 'Not signed in' };
  // Scoped to my own row in the statement as well as by policy. Without
  // the profile_id filter this would ask to delete everybody's.
  const { error } = await supabase
    .from('deal_activity_acks')
    .delete()
    .eq('activity_id', activityId)
    .eq('profile_id', uid);
  if (error) {
    if (isMissingTableError(error, 'deal_activity_acks')) {
      return { ok: false, reason: 'Acknowledgements are not enabled yet — migration-258 has not been applied.' };
    }
    handleError('removeDealActivityAck', error);
  }
  return { ok: true };
}

/**
 * Exactly the columns dbSignatureRequestToApp reads, and no others.
 *
 * This was `select('*')`, which on this table is ~50 columns of which
 * the mapper uses about 30. Measured on 2026-10-22: 375 rows came to
 * 2,046 kB, and the browser threw most of it away.
 *
 * Two of the columns it was throwing away are SIGNING TOKENS. They are
 * bearer credentials for the public signing URL — whoever holds one can
 * open the document and sign it — and every signed-in browser was being
 * handed every one of them on page load, to use for nothing. No reader
 * has ever existed: the mapper does not map them, and the signing page
 * gets its own row server-side. So this list is a payload fix that
 * happens to close a needless exposure, and the tokens must stay off it.
 *
 * `offer_terms` is NOT here, and it was the bulk of what the explicit
 * list first left behind: 1,021 kB of the 2,046. The board needs one
 * string out of it — whether a request is an offer or one of the seller
 * forms — so it selects the derived `signing_purpose` column instead
 * (migration-252), and the mapper rebuilds the shape the readers
 * expect. The deal page, which shows the amount that was offered, gets
 * the real column through getDealSignatureRequestsForDeal.
 */
const SIGNATURE_REQUEST_APP_COLUMNS = [
  'id', 'deal_id', 'signature_request_id', 'doc_title', 'file_name',
  'seller_name', 'seller_email', 'counter_signer_name', 'counter_signer_email',
  'status', 'test_mode', 'created_at', 'sent_at', 'viewed_at', 'signed_at',
  'declined_at', 'declined_reason', 'canceled_at', 'voided_at', 'expires_at',
  'expiring_notified_at', 'last_event_at', 'last_event_type',
  'signed_document_id', 'signed_document_storage_path',
  'unsigned_storage_path', 'partial_storage_path',
  'signing_purpose', 'cc_emails',
  'created_by_user_id', 'created_by_name', 'created_by_email', 'meta',
].join(', ');

// The same columns plus the one the board does without. Used when a deal
// is opened, so the offer amount can be shown on the deal it belongs to.
const SIGNATURE_REQUEST_FULL_COLUMNS = `${SIGNATURE_REQUEST_APP_COLUMNS}, offer_terms`;

/** Every signature request for ONE deal, with the offer terms. */
async function getDealSignatureRequestsForDeal(dealId) {
  if (!dealId) return [];
  const { data, error } = await supabase
    .from('deal_signature_requests').select(SIGNATURE_REQUEST_FULL_COLUMNS)
    .eq('deal_id', dealId)
    .order('created_at', { ascending: false }).order('id', { ascending: false });
  if (error) {
    handleError('getDealSignatureRequestsForDeal', error);
    return [];
  }
  return (data || []).map(dbSignatureRequestToApp);
}

async function getDealSignatureRequests() {
  const rows = await fetchAllRows('getDealSignatureRequests', () => supabase
    .from('deal_signature_requests').select(SIGNATURE_REQUEST_APP_COLUMNS)
    .order('created_at', { ascending: false }).order('id', { ascending: false }));
  return rows.map(dbSignatureRequestToApp);
}

async function addDealTodo(entry) {
  const row = {
    id: entry.id,
    deal_id: entry.dealId,
    text: entry.text,
    done: !!entry.done,
    created_at: entry.createdAt || new Date().toISOString(),
    completed_at: entry.completedAt || null,
    created_by: entry.createdBy || null,
    created_by_name: entry.createdByName || null,
    assignee_id: entry.assigneeId || null,
    assignee_name: entry.assigneeName || null,
    category: entry.category || 'general',
    note: entry.note || null,
    position: Number(entry.position) || 0,
  };
  const { data, error } = await supabase.from('deal_todos').insert(row).select().single();
  if (error) handleError('addDealTodo', error);
  return data ? dbTodoToApp(data) : null;
}

async function updateDealTodo(id, patch) {
  const row = {};
  if ('text' in patch)         row.text = patch.text;
  if ('done' in patch)         row.done = !!patch.done;
  if ('completedAt' in patch)  row.completed_at = patch.completedAt;
  if ('assigneeId' in patch)   row.assignee_id = patch.assigneeId;
  if ('assigneeName' in patch) row.assignee_name = patch.assigneeName;
  if ('note' in patch)         row.note = patch.note || null;
  if ('category' in patch)     row.category = patch.category || 'general';
  if ('position' in patch)     row.position = Number(patch.position) || 0;
  const { data, error } = await supabase
    .from('deal_todos').update(row).eq('id', id).select().single();
  if (error) handleError('updateDealTodo', error);
  return data ? dbTodoToApp(data) : null;
}

async function deleteDealTodo(id) {
  const { error } = await supabase.from('deal_todos').delete().eq('id', id);
  if (error) handleError('deleteDealTodo', error);
  return true;
}

async function addDealActivity(entry) {
  const lifted = ['id','dealId','type','text','actorId','actorName','createdAt','archived'];
  const meta = {};
  for (const [k, v] of Object.entries(entry || {})) {
    if (!lifted.includes(k)) meta[k] = v;
  }
  const row = {
    id: entry.id,
    deal_id: entry.dealId,
    type: entry.type || 'note',
    text: entry.text || null,
    actor_id: entry.actorId || null,
    actor_name: entry.actorName || null,
    created_at: entry.createdAt || new Date().toISOString(),
    archived: !!entry.archived,
    meta,
  };
  const { data, error } = await supabase.from('deal_activities').insert(row).select().single();
  if (error) handleError('addDealActivity', error);
  return data ? dbActivityToApp(data) : null;
}

async function deleteDealActivity(id) {
  const { error } = await supabase.from('deal_activities').delete().eq('id', id);
  if (error) handleError('deleteDealActivity', error);
  return true;
}

async function upsertDealSignatureRequest(entry) {
  const lifted = new Set([
    'id','dealId','signatureRequestId','docTitle','fileName',
    'sellerName','sellerEmail','counterSignerName','counterSignerEmail',
    'status','testMode',
    'createdAt','sentAt','viewedAt','signedAt','declinedAt','declineReason',
    'canceledAt','voidedAt','expiresAt','expiringNotifiedAt','lastEventAt',
    'lastEventType','signedDocumentId','signedDocumentStoragePath',
    'ccEmails','createdByUserId','createdByName','createdByEmail',
  ]);
  const meta = {};
  for (const [k, v] of Object.entries(entry || {})) {
    if (!lifted.has(k)) meta[k] = v;
  }
  const row = {
    id: entry.id,
    deal_id: entry.dealId,
    signature_request_id: entry.signatureRequestId || null,
    doc_title: entry.docTitle || null,
    file_name: entry.fileName || null,
    seller_name: entry.sellerName || null,
    seller_email: entry.sellerEmail || null,
    counter_signer_name: entry.counterSignerName || null,
    counter_signer_email: entry.counterSignerEmail || null,
    status: entry.status || 'draft',
    test_mode: !!entry.testMode,
    created_at: entry.createdAt || new Date().toISOString(),
    sent_at: entry.sentAt || null,
    viewed_at: entry.viewedAt || null,
    signed_at: entry.signedAt || null,
    declined_at: entry.declinedAt || null,
    declined_reason: entry.declineReason || null,
    canceled_at: entry.canceledAt || null,
    voided_at: entry.voidedAt || null,
    expires_at: entry.expiresAt || null,
    expiring_notified_at: entry.expiringNotifiedAt || null,
    last_event_at: entry.lastEventAt || null,
    last_event_type: entry.lastEventType || null,
    signed_document_id: entry.signedDocumentId || null,
    signed_document_storage_path: entry.signedDocumentStoragePath || null,
    cc_emails: Array.isArray(entry.ccEmails) ? entry.ccEmails : [],
    created_by_user_id: entry.createdByUserId || null,
    created_by_name: entry.createdByName || null,
    created_by_email: entry.createdByEmail || null,
    meta,
  };
  const { data, error } = await supabase
    .from('deal_signature_requests')
    .upsert(row, { onConflict: 'id' })
    // Naming the columns, because a bare .select() is `*` — and `*`
    // here includes seller_sign_token and counter_sign_token. Those are
    // bearer credentials: whoever holds one can open the seller's
    // signing page and sign as them. Every other read of this table
    // goes through SIGNATURE_REQUEST_APP_COLUMNS, which leaves them out
    // on purpose; this one call was handing them to the browser on
    // every upsert.
    .select(SIGNATURE_REQUEST_APP_COLUMNS)
    .single();
  if (error) handleError('upsertDealSignatureRequest', error);
  return data ? dbSignatureRequestToApp(data) : null;
}

async function deleteDealSignatureRequest(id) {
  const { error } = await supabase.from('deal_signature_requests').delete().eq('id', id);
  if (error) handleError('deleteDealSignatureRequest', error);
  return true;
}

async function getCertifiedMailJobs(limit = 500) {
  const { data, error } = await supabase
    .from('certified_mail_jobs')
    .select('*, certified_mail_documents(*), certified_mail_events(*)')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) {
    if (isMissingTableError(error, 'certified_mail_jobs')) {
      console.warn('[supabaseStore] certified_mail_jobs table is missing; run migration-126-certified-mail.sql to enable mail tracking.');
      return [];
    }
    handleError('getCertifiedMailJobs', error);
  }
  return (data || []).map(dbCertifiedMailJobToApp).filter(Boolean);
}

async function updateCertifiedMailJob(id, patch = {}) {
  const row = {};
  if ('status' in patch) row.status = patch.status;
  if ('providerStatus' in patch) row.provider_status = patch.providerStatus;
  if ('trackingNumber' in patch) row.tracking_number = patch.trackingNumber;
  if ('trackingUrl' in patch) row.tracking_url = patch.trackingUrl;
  if ('returnTrackingNumber' in patch) row.return_tracking_number = patch.returnTrackingNumber;
  if ('returnTrackingUrl' in patch) row.return_tracking_url = patch.returnTrackingUrl;
  if ('mailedAt' in patch) row.mailed_at = patch.mailedAt;
  if ('deliveredAt' in patch) row.delivered_at = patch.deliveredAt;
  if ('returnReceivedAt' in patch) row.return_received_at = patch.returnReceivedAt;
  if ('meta' in patch) row.meta = patch.meta && typeof patch.meta === 'object' ? patch.meta : {};
  const { data, error } = await supabase
    .from('certified_mail_jobs')
    .update(row)
    .eq('id', id)
    .select('*, certified_mail_documents(*), certified_mail_events(*)')
    .single();
  if (error) handleError('updateCertifiedMailJob', error);
  return data ? dbCertifiedMailJobToApp(data) : null;
}

async function addDeal(d) {
  const uid = await currentUserId();
  // `.select().single()` forces the round-trip to return the inserted row
  // — if the INSERT silently fails (RLS block, FK mismatch, etc.) we get
  // a proper error instead of a fake-success. Callers rely on this to
  // gate navigation / activity logs on actual DB persistence.
  const { data, error } = await supabase
    .from('deals')
    .insert({ ...appDealToDb(d), id: d.id, created_by: uid })
    .select()
    .single();
  if (error) handleError('addDeal', error);
  return data ? dbDealToApp(data) : null;
}

async function updateDeal(id, updates, options = {}) {
  const patch = appDealToDb(updates || {});
  if (Object.keys(patch).length === 0) {
    const existing = await getDealFull(id);
    if (!existing) handleError('updateDeal', new Error('Deal not found or not writable'));
    return existing;
  }
  let query = supabase
    .from('deals')
    .update(patch)
    .eq('id', id);
  if (options?.expectedUpdatedAt) {
    query = query.eq('updated_at', options.expectedUpdatedAt);
  }
  const { data, error } = await query.select();
  if (error) handleError('updateDeal', error);
  const savedRow = Array.isArray(data) ? data[0] : data;
  if (!savedRow) {
    const reason = options?.expectedUpdatedAt
      ? 'Deal changed before this save completed. Refresh the deal and review the latest changes before saving again.'
      : 'Deal update did not return a persisted row';
    if (options?.expectedUpdatedAt) throw new Error(reason);
    handleError('updateDeal', new Error(reason));
  }
  if (hasCriticalDealAuditColumn(patch)) {
    await recordAuditEventQuietly({
      action: auditActionForDealPatch(patch),
      entityType: 'deal',
      entityId: id,
      dealId: id,
      afterData: patch,
      metadata: {
        expectedUpdatedAt: options?.expectedUpdatedAt || null,
        returnedUpdatedAt: savedRow.updated_at || null,
      },
    });
  }
  return dbDealToApp(savedRow);
}

/**
 * An acquisition manager putting their name on an unclaimed deal.
 *
 * Through an RPC rather than an update, because the condition is the
 * whole point: `buy_side_agent_id is null` inside one statement is what
 * makes two managers clicking the same card at the same moment resolve
 * to one winner. A read-then-write here would let the second click
 * quietly overwrite the first, and the loser would never know — they
 * would simply be working somebody else's deal.
 *
 * The RPC raises rather than returning nothing on a refusal, and says
 * which refusal it is: "already claimed" and "no such deal" send
 * somebody to very different places.
 */
async function claimDeal(id) {
  const { data, error } = await supabase.rpc('claim_deal', { p_deal_id: id });
  if (error) handleError('claimDeal', error);
  const row = Array.isArray(data) ? data[0] : data;
  await recordAuditEventQuietly({
    action: 'deal.claimed',
    entityType: 'deal',
    entityId: id,
    dealId: id,
  });
  return row ? dbDealToApp(row) : null;
}

async function deleteDeal(id) {
  const { data: before } = await supabase
    .from('deals')
    .select('id,owner_name,stage,contact_id,updated_at')
    .eq('id', id)
    .maybeSingle();
  const { error } = await supabase.from('deals').delete().eq('id', id);
  if (error) handleError('deleteDeal', error);
  await recordAuditEventQuietly({
    action: 'deal.deleted',
    entityType: 'deal',
    entityId: id,
    dealId: id,
    beforeData: before || null,
  });
}

// ── OFFERS ───────────────────────────────────────────────────────────────────

export function dbOfferToApp(o) {
  return {
    id: o.id, dealId: o.deal_id, offerAmount: o.offer_amount,
    offerDate: o.offer_date, expirationDate: o.expiration_date,
    status: o.status, counterAmount: o.counter_amount,
    notes: o.notes, letterGenerated: o.letter_generated, createdAt: o.created_at,
  };
}

async function getOffers() {
  const rows = await fetchAllRows('getOffers', () => supabase
    .from('offers').select('*')
    .order('created_at', { ascending: false }).order('id', { ascending: false }));
  return rows.map(dbOfferToApp);
}

async function addOffer(o) {
  const uid = await currentUserId();
  const { data, error } = await supabase
    .from('offers')
    .insert({
      id: o.id, deal_id: o.dealId, offer_amount: o.offerAmount,
      offer_date: o.offerDate, expiration_date: o.expirationDate,
      status: o.status || 'pending', counter_amount: o.counterAmount,
      notes: o.notes, letter_generated: o.letterGenerated || false, created_by: uid,
    })
    .select()
    .single();
  if (error) handleError('addOffer', error);
  return data ? dbOfferToApp(data) : null;
}

async function updateOffer(id, updates) {
  const map = {
    offerAmount: 'offer_amount', offerDate: 'offer_date', expirationDate: 'expiration_date',
    status: 'status', counterAmount: 'counter_amount', notes: 'notes', letterGenerated: 'letter_generated',
  };
  const patch = Object.fromEntries(
    Object.entries(map).filter(([k]) => updates[k] !== undefined).map(([k, v]) => [v, updates[k]])
  );
  const { error } = await supabase.from('offers').update(patch).eq('id', id);
  if (error) handleError('updateOffer', error);
}

async function deleteOffer(id) {
  const { error } = await supabase.from('offers').delete().eq('id', id);
  if (error) handleError('deleteOffer', error);
}

// ── BUYER PROFILES ───────────────────────────────────────────────────────────

function appBuyerToDb(b) {
  const map = {
    name: 'name', company: 'company', email: 'email', phone: 'phone',
    buyerType: 'buyer_type', basins: 'basins', plays: 'plays', counties: 'counties',
    priceRangeMin: 'price_range_min', priceRangeMax: 'price_range_max', notes: 'notes',
    assignedAgentId: 'assigned_agent_id',
    buyerMapAccess: 'buyer_map_access', buyerMapDealIds: 'buyer_map_deal_ids',
  };
  // Numeric columns can't accept empty strings — Postgres rejects
  // them with "invalid input syntax for type numeric". Coerce empty /
  // blank values to null so every caller (form, inline quick-add,
  // imports) is safe regardless of the shape they pass in.
  const NUMERIC_KEYS = new Set(['priceRangeMin', 'priceRangeMax']);
  const toDbValue = (k, v) => {
    if (!NUMERIC_KEYS.has(k)) return v;
    if (v === '' || v === null || v === undefined) return null;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };
  return Object.fromEntries(
    Object.entries(map)
      .filter(([k]) => b[k] !== undefined)
      .map(([k, v]) => [v, toDbValue(k, b[k])])
  );
}

async function getBuyers() {
  const rows = await fetchAllRows('getBuyers', () => supabase
    .from('buyer_profiles').select('*')
    .order('created_at', { ascending: false }).order('id', { ascending: false }));
  return rows.map(dbBuyerToApp);
}

async function addBuyer(b) {
  const uid = await currentUserId();
  const { data, error } = await supabase
    .from('buyer_profiles')
    .insert({ ...appBuyerToDb(b), id: b.id, created_by: uid })
    .select()
    .single();
  if (error) handleError('addBuyer', error);
  return data ? dbBuyerToApp(data) : null;
}

async function updateBuyer(id, updates) {
  const { error } = await supabase.from('buyer_profiles').update(appBuyerToDb(updates)).eq('id', id);
  if (error) handleError('updateBuyer', error);
}

async function deleteBuyer(id) {
  const { error } = await supabase.from('buyer_profiles').delete().eq('id', id);
  if (error) handleError('deleteBuyer', error);
}

// ── INVESTOR PROFILES & ENTITIES ─────────────────────────────────────────────
//
// Investors come in deal by deal. Paraclete and the investor jointly own
// an entity, and that entity takes title — so these rows feed both the
// mineral deed (the entity's legal name) and the commission engine (the
// rep on 10% of margin).

function appInvestorToDb(i) {
  const map = {
    name: 'name', company: 'company', email: 'email', phone: 'phone',
    investorType: 'investor_type', status: 'status', notes: 'notes',
    assignedRepId: 'assigned_rep_id', repCommissionRate: 'rep_commission_rate',
  };
  // Same trap as buyers: a numeric column rejects '' with "invalid input
  // syntax for type numeric", and a cleared rate field sends ''.
  const NUMERIC_KEYS = new Set(['repCommissionRate']);
  const toDbValue = (k, v) => {
    if (!NUMERIC_KEYS.has(k)) return v;
    if (v === '' || v === null || v === undefined) return null;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };
  return Object.fromEntries(
    Object.entries(map).filter(([k]) => i[k] !== undefined).map(([k, v]) => [v, toDbValue(k, i[k])]),
  );
}

function appInvestorEntityToDb(e) {
  const map = {
    investorId: 'investor_id', legalName: 'legal_name',
    entityDescriptor: 'entity_descriptor', address: 'address',
    signerName: 'signer_name', signerTitle: 'signer_title',
    stateOfFormation: 'state_of_formation',
    paracleteOwnershipPct: 'paraclete_ownership_pct',
    investorOwnershipPct: 'investor_ownership_pct',
    repOwnershipPct: 'rep_ownership_pct',
    active: 'active', notes: 'notes',
  };
  const NUMERIC_KEYS = new Set(['paracleteOwnershipPct', 'investorOwnershipPct', 'repOwnershipPct']);
  const toDbValue = (k, v) => {
    if (!NUMERIC_KEYS.has(k)) return v;
    if (v === '' || v === null || v === undefined) return null;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };
  return Object.fromEntries(
    Object.entries(map).filter(([k]) => e[k] !== undefined).map(([k, v]) => [v, toDbValue(k, e[k])]),
  );
}

async function getInvestors() {
  const rows = await fetchAllRows('getInvestors', () => supabase
    .from('investor_profiles').select('*')
    .order('created_at', { ascending: false }).order('id', { ascending: false }));
  return rows.map(dbInvestorToApp);
}

async function addInvestor(i) {
  const uid = await currentUserId();
  const { data, error } = await supabase
    .from('investor_profiles')
    .insert({ ...appInvestorToDb(i), id: i.id, created_by: uid })
    .select()
    .single();
  if (error) handleError('addInvestor', error);
  return data ? dbInvestorToApp(data) : null;
}

async function updateInvestor(id, updates) {
  const { error } = await supabase.from('investor_profiles').update(appInvestorToDb(updates)).eq('id', id);
  if (error) handleError('updateInvestor', error);
}

async function deleteInvestor(id) {
  const { error } = await supabase.from('investor_profiles').delete().eq('id', id);
  if (error) handleError('deleteInvestor', error);
}

async function getInvestorEntities() {
  const rows = await fetchAllRows('getInvestorEntities', () => supabase
    .from('investor_entities').select('*')
    .order('created_at', { ascending: false }).order('id', { ascending: false }));
  return rows.map(dbInvestorEntityToApp);
}

async function addInvestorEntity(e) {
  const uid = await currentUserId();
  const { data, error } = await supabase
    .from('investor_entities')
    .insert({ ...appInvestorEntityToDb(e), id: e.id, created_by: uid })
    .select()
    .single();
  if (error) handleError('addInvestorEntity', error);
  return data ? dbInvestorEntityToApp(data) : null;
}

async function updateInvestorEntity(id, updates) {
  const { error } = await supabase.from('investor_entities').update(appInvestorEntityToDb(updates)).eq('id', id);
  if (error) handleError('updateInvestorEntity', error);
}

async function deleteInvestorEntity(id) {
  const { error } = await supabase.from('investor_entities').delete().eq('id', id);
  if (error) handleError('deleteInvestorEntity', error);
}

// ── ASSET INCOME ─────────────────────────────────────────────────────────────
//
// What a held asset has actually paid: royalty cheques, lease bonuses,
// shut-in and delay rentals. One row per receipt, not a running total,
// because the questions asked of this are "what did this tract pay last
// year" and "what did this operator pay us" — a total answers neither.

export function dbAssetIncomeToApp(r) {
  return {
    id: r.id, dealId: r.deal_id,
    // Null means the receipt covers the deal as a whole: an operator's
    // cheque often does not break out by tract.
    rowId: r.row_id || null,
    kind: r.kind || 'royalty',
    amount: r.amount == null ? 0 : Number(r.amount),
    receivedOn: r.received_on || '',
    // What the money was FOR, which is not when it arrived.
    periodStart: r.period_start || '',
    periodEnd: r.period_end || '',
    operator: r.operator || '', reference: r.reference || '', notes: r.notes || '',
    createdAt: r.created_at, updatedAt: r.updated_at,
  };
}

function appAssetIncomeToDb(r) {
  const map = {
    dealId: 'deal_id', rowId: 'row_id', kind: 'kind', amount: 'amount',
    receivedOn: 'received_on', periodStart: 'period_start', periodEnd: 'period_end',
    operator: 'operator', reference: 'reference', notes: 'notes',
  };
  // Same trap as every other table here: a numeric or date column
  // answers '' with "invalid input syntax", and a cleared optional date
  // field sends ''.
  const EMPTY_TO_NULL = new Set(['amount', 'receivedOn', 'periodStart', 'periodEnd', 'rowId']);
  const toDbValue = (k, v) => {
    if (!EMPTY_TO_NULL.has(k)) return v;
    if (v === '' || v === null || v === undefined) return null;
    if (k !== 'amount') return v;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };
  return Object.fromEntries(
    Object.entries(map).filter(([k]) => r[k] !== undefined).map(([k, v]) => [v, toDbValue(k, r[k])]),
  );
}

async function getAssetIncome() {
  const rows = await fetchAllRows('getAssetIncome', () => supabase
    .from('asset_income').select('*')
    .order('received_on', { ascending: false }).order('id', { ascending: false }));
  return rows.map(dbAssetIncomeToApp);
}

async function addAssetIncome(r) {
  const uid = await currentUserId();
  const { data, error } = await supabase
    .from('asset_income')
    .insert({ ...appAssetIncomeToDb(r), id: r.id, created_by: uid })
    .select()
    .single();
  if (error) handleError('addAssetIncome', error);
  return data ? dbAssetIncomeToApp(data) : null;
}

async function updateAssetIncome(id, updates) {
  const { error } = await supabase.from('asset_income').update(appAssetIncomeToDb(updates)).eq('id', id);
  if (error) handleError('updateAssetIncome', error);
}

async function deleteAssetIncome(id) {
  const { error } = await supabase.from('asset_income').delete().eq('id', id);
  if (error) handleError('deleteAssetIncome', error);
}

// ── DEAL PACKAGES ────────────────────────────────────────────────────────────

export function dbPackageToApp(p) {
  return {
    id: p.id, name: p.name, description: p.description,
    dealIds: p.deal_ids || [], buyerId: p.buyer_id,
    shareable: p.shareable, status: p.status,
    createdAt: p.created_at, updatedAt: p.updated_at,
  };
}

async function getPackages() {
  const rows = await fetchAllRows('getPackages', () => supabase
    .from('deal_packages').select('*')
    .order('created_at', { ascending: false }).order('id', { ascending: false }));
  return rows.map(dbPackageToApp);
}

async function addPackage(p) {
  const uid = await currentUserId();
  const { data, error } = await supabase
    .from('deal_packages')
    .insert({
      id: p.id, name: p.name, description: p.description,
      deal_ids: p.dealIds || [], buyer_id: p.buyerId,
      shareable: p.shareable || false, status: p.status || 'draft', created_by: uid,
    })
    .select()
    .single();
  if (error) handleError('addPackage', error);
  return data ? dbPackageToApp(data) : null;
}

async function updatePackage(id, updates) {
  const map = {
    name: 'name', description: 'description', dealIds: 'deal_ids',
    buyerId: 'buyer_id', shareable: 'shareable', status: 'status',
  };
  const patch = Object.fromEntries(
    Object.entries(map).filter(([k]) => updates[k] !== undefined).map(([k, v]) => [v, updates[k]])
  );
  const { error } = await supabase.from('deal_packages').update(patch).eq('id', id);
  if (error) handleError('updatePackage', error);
}

async function deletePackage(id) {
  const { error } = await supabase.from('deal_packages').delete().eq('id', id);
  if (error) handleError('deletePackage', error);
}

// ── CLOSINGS ─────────────────────────────────────────────────────────────────

export function dbClosingToApp(c) {
  return {
    id: c.id, dealId: c.deal_id, status: c.status,
    closingDate: c.closing_date, titleCompany: c.title_company,
    notes: c.notes, createdAt: c.created_at, updatedAt: c.updated_at,
  };
}

async function getClosings() {
  const rows = await fetchAllRows('getClosings', () => supabase
    .from('closings').select('*')
    .order('closing_date', { ascending: true }).order('id', { ascending: true }));
  return rows.map(dbClosingToApp);
}

async function addClosing(c) {
  const uid = await currentUserId();
  const { data, error } = await supabase
    .from('closings')
    .insert({
      id: c.id, deal_id: c.dealId, status: c.status || 'Scheduled',
      closing_date: c.closingDate, title_company: c.titleCompany,
      notes: c.notes, created_by: uid,
    })
    .select()
    .single();
  if (error) handleError('addClosing', error);
  return data ? dbClosingToApp(data) : null;
}

async function updateClosing(id, updates) {
  const map = {
    status: 'status', closingDate: 'closing_date',
    titleCompany: 'title_company', notes: 'notes',
  };
  const patch = Object.fromEntries(
    Object.entries(map).filter(([k]) => updates[k] !== undefined).map(([k, v]) => [v, updates[k]])
  );
  const { error } = await supabase.from('closings').update(patch).eq('id', id);
  if (error) handleError('updateClosing', error);
}

async function deleteClosing(id) {
  const { error } = await supabase.from('closings').delete().eq('id', id);
  if (error) handleError('deleteClosing', error);
}

// ── CLOSING TASKS ────────────────────────────────────────────────────────────

export function dbTaskToApp(t) {
  return {
    id: t.id, closingId: t.closing_id, title: t.title,
    assignedTo: t.assigned_to, dueDate: t.due_date,
    completed: t.completed, completedAt: t.completed_at, createdAt: t.created_at,
  };
}

async function getClosingTasks() {
  const rows = await fetchAllRows('getClosingTasks', () => supabase
    .from('closing_tasks').select('*')
    .order('created_at', { ascending: true }).order('id', { ascending: true }));
  return rows.map(dbTaskToApp);
}

async function addClosingTask(t) {
  const { data, error } = await supabase
    .from('closing_tasks')
    .insert({
      id: t.id, closing_id: t.closingId, title: t.title,
      assigned_to: t.assignedTo || null, due_date: t.dueDate || null,
      completed: false,
    })
    .select()
    .single();
  if (error) handleError('addClosingTask', error);
  return data ? dbTaskToApp(data) : null;
}

async function updateClosingTask(id, updates) {
  const map = {
    title: 'title', assignedTo: 'assigned_to', dueDate: 'due_date',
    completed: 'completed', completedAt: 'completed_at',
  };
  const patch = Object.fromEntries(
    Object.entries(map).filter(([k]) => updates[k] !== undefined).map(([k, v]) => [v, updates[k]])
  );
  const { error } = await supabase.from('closing_tasks').update(patch).eq('id', id);
  if (error) handleError('updateClosingTask', error);
}

async function deleteClosingTask(id) {
  const { error } = await supabase.from('closing_tasks').delete().eq('id', id);
  if (error) handleError('deleteClosingTask', error);
}

// ── STR PRICING ─────────────────────────────────────────────────────────────

// ── NOTIFICATIONS (per-user in-app inbox) ──────────────────────────────────

async function getNotifications({ limit = 50 } = {}) {
  const uid = await currentUserId();
  if (!uid) return [];
  const { data, error } = await supabase
    .from('notifications')
    .select('id, kind, title, message, link_path, meta, read_at, created_at')
    .eq('user_id', uid)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) {
    if (error.code === '42P01' || error.message?.includes('does not exist')) return [];
    console.warn('[supabaseStore] getNotifications error:', error.message);
    return [];
  }
  return (data || []).map((r) => ({
    id: r.id,
    kind: r.kind,
    title: r.title,
    message: r.message || '',
    linkPath: r.link_path || null,
    meta: r.meta || {},
    readAt: r.read_at,
    createdAt: r.created_at,
  }));
}

async function markNotificationRead(id) {
  const { error } = await supabase
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', id);
  if (error) console.warn('[supabaseStore] markNotificationRead error:', error.message);
}

async function markAllNotificationsRead() {
  const uid = await currentUserId();
  if (!uid) return;
  const { error } = await supabase
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('user_id', uid)
    .is('read_at', null);
  if (error) console.warn('[supabaseStore] markAllNotificationsRead error:', error.message);
}

async function deleteNotification(id) {
  const { error } = await supabase.from('notifications').delete().eq('id', id);
  if (error) console.warn('[supabaseStore] deleteNotification error:', error.message);
}

// ── MOBILE PUSH TOKENS ──────────────────────────────────────────────────────

function normalizePushPlatform(platform) {
  const value = String(platform || '').trim().toLowerCase();
  if (value === 'ios' || value === 'android' || value === 'web') return value;
  return 'web';
}

function normalizePushProvider(provider) {
  const value = String(provider || '').trim().toLowerCase();
  if (['capacitor', 'apns', 'fcm', 'web-push', 'manual'].includes(value)) return value;
  return 'capacitor';
}

function nullablePushString(value) {
  const text = value == null ? '' : String(value).trim();
  return text || null;
}

function dbPushDeviceTokenToApp(r) {
  if (!r) return null;
  return {
    id: r.id,
    tenantId: r.tenant_id,
    userId: r.user_id,
    platform: r.platform,
    provider: r.provider,
    tokenHash: r.token_hash,
    deviceId: r.device_id || '',
    deviceName: r.device_name || '',
    appVersion: r.app_version || '',
    appBuild: r.app_build || '',
    osVersion: r.os_version || '',
    locale: r.locale || '',
    timezone: r.timezone || '',
    enabled: r.enabled !== false,
    lastSeenAt: r.last_seen_at || null,
    disabledAt: r.disabled_at || null,
    revokedAt: r.revoked_at || null,
    failureCount: Number(r.failure_count) || 0,
    lastError: r.last_error || '',
    createdAt: r.created_at || null,
    updatedAt: r.updated_at || null,
    meta: r.meta && typeof r.meta === 'object' ? r.meta : {},
  };
}

function pushDeviceTokenRow(input, uid, tenantId) {
  const token = nullablePushString(input?.token || input?.providerToken || input?.value);
  if (!token) throw new Error('Push notification token is required.');
  if (token.length < 16) throw new Error('Push notification token is too short.');
  return {
    tenant_id: tenantId,
    user_id: uid,
    platform: normalizePushPlatform(input?.platform),
    provider: normalizePushProvider(input?.provider),
    token,
    device_id: nullablePushString(input?.deviceId || input?.device_id),
    device_name: nullablePushString(input?.deviceName || input?.device_name),
    app_version: nullablePushString(input?.appVersion || input?.app_version),
    app_build: nullablePushString(input?.appBuild || input?.app_build),
    os_version: nullablePushString(input?.osVersion || input?.os_version),
    locale: nullablePushString(input?.locale),
    timezone: nullablePushString(input?.timezone),
    enabled: true,
    disabled_at: null,
    revoked_at: null,
    last_seen_at: new Date().toISOString(),
    last_error: null,
    meta: input?.meta && typeof input.meta === 'object' ? input.meta : {},
  };
}

async function registerPushDeviceToken(input = {}) {
  const uid = await currentUserId();
  const tenantId = await currentTenantId();
  if (!uid) throw new Error('Sign in before registering this device for push notifications.');
  if (!tenantId) throw new Error('Your profile is missing a tenant id. Push notifications were not registered.');

  const row = pushDeviceTokenRow(input, uid, tenantId);

  if (row.device_id) {
    const { data: existing, error: existingError } = await supabase
      .from('push_device_tokens')
      .select('id')
      .eq('user_id', uid)
      .eq('platform', row.platform)
      .eq('device_id', row.device_id)
      .is('revoked_at', null)
      .maybeSingle();
    if (existingError) {
      if (isMissingTableError(existingError, 'push_device_tokens')) {
        throw new Error('Push notification tables are missing. Run supabase/migration-127-mobile-push-notifications.sql.');
      }
      handleError('registerPushDeviceToken.lookup', existingError);
    }
    if (existing?.id) {
      const { data, error } = await supabase
        .from('push_device_tokens')
        .update(row)
        .eq('id', existing.id)
        .select('*')
        .single();
      if (error) handleError('registerPushDeviceToken.update', error);
      return dbPushDeviceTokenToApp(data);
    }
  }

  const { data, error } = await supabase
    .from('push_device_tokens')
    .upsert(row, { onConflict: 'tenant_id,token_hash' })
    .select('*')
    .single();
  if (error) {
    if (isMissingTableError(error, 'push_device_tokens')) {
      throw new Error('Push notification tables are missing. Run supabase/migration-127-mobile-push-notifications.sql.');
    }
    handleError('registerPushDeviceToken', error);
  }
  return dbPushDeviceTokenToApp(data);
}

async function listPushDeviceTokens() {
  const uid = await currentUserId();
  if (!uid) return [];
  const { data, error } = await supabase
    .from('push_device_tokens')
    .select('*')
    .eq('user_id', uid)
    .is('revoked_at', null)
    .order('last_seen_at', { ascending: false });
  if (error) {
    if (isMissingTableError(error, 'push_device_tokens')) return [];
    console.warn('[supabaseStore] listPushDeviceTokens error:', error.message);
    return [];
  }
  return (data || []).map(dbPushDeviceTokenToApp);
}

async function retirePushDeviceToken(target = {}) {
  const uid = await currentUserId();
  if (!uid) return null;
  const now = new Date().toISOString();
  let query = supabase
    .from('push_device_tokens')
    .update({
      enabled: false,
      disabled_at: now,
      revoked_at: now,
      last_error: nullablePushString(target.reason) || null,
    })
    .eq('user_id', uid);

  if (target.id) {
    query = query.eq('id', target.id);
  } else if (target.token || target.providerToken || target.value) {
    query = query.eq('token', String(target.token || target.providerToken || target.value));
  } else if (target.deviceId || target.device_id) {
    query = query
      .eq('platform', normalizePushPlatform(target.platform))
      .eq('device_id', String(target.deviceId || target.device_id));
  } else {
    throw new Error('A push device token id, token, or device id is required.');
  }

  const { data, error } = await query.select('*').maybeSingle();
  if (error) {
    if (isMissingTableError(error, 'push_device_tokens')) return null;
    handleError('retirePushDeviceToken', error);
  }
  return dbPushDeviceTokenToApp(data);
}

async function getNmSTRPricingReferences() {
  const { data, error } = await supabase
    .from('nm_str_pricing_reference')
    .select(`
      id, county, section, township, range, land_key, model_version, calculation_status,
      price_per_nra_low, price_per_nra_mid, price_per_nra_high,
      price_per_nma_low, price_per_nma_mid, price_per_nma_high,
      negotiation_buy_target_nra, negotiation_buy_target_nma,
      buyer_resale_target_nra, buyer_resale_target_nma,
      engineering_pv10_per_nra, engineering_pv10_per_nma,
      confidence_score, confidence_label,
      production_score, permit_score, lease_sale_score, buyer_comp_score, geology_score, federal_land_score,
      wti_price, henry_hub_price, data_as_of, computed_at, stale_after,
      explanation, metrics, source_counts, manual_override, override_reason, updated_at
    `);
  if (error) {
    if (error.code === '42P01' || error.message?.includes('does not exist')) return [];
    console.warn('[supabaseStore] getNmSTRPricingReferences error:', error.message);
    return [];
  }
  return (data || []).map((r) => ({
    id: r.id,
    state: 'NM',
    county: r.county || '',
    section: r.section,
    township: r.township,
    range: r.range,
    landKey: r.land_key || '',
    modelVersion: r.model_version || '',
    status: r.calculation_status || '',
    pricePerNraLow: r.price_per_nra_low == null ? null : Number(r.price_per_nra_low),
    pricePerNraMid: r.price_per_nra_mid == null ? null : Number(r.price_per_nra_mid),
    pricePerNraHigh: r.price_per_nra_high == null ? null : Number(r.price_per_nra_high),
    pricePerNmaLow: r.price_per_nma_low == null ? null : Number(r.price_per_nma_low),
    pricePerNmaMid: r.price_per_nma_mid == null ? null : Number(r.price_per_nma_mid),
    pricePerNmaHigh: r.price_per_nma_high == null ? null : Number(r.price_per_nma_high),
    negotiationBuyTargetNra: r.negotiation_buy_target_nra == null ? null : Number(r.negotiation_buy_target_nra),
    negotiationBuyTargetNma: r.negotiation_buy_target_nma == null ? null : Number(r.negotiation_buy_target_nma),
    buyerResaleTargetNra: r.buyer_resale_target_nra == null ? null : Number(r.buyer_resale_target_nra),
    buyerResaleTargetNma: r.buyer_resale_target_nma == null ? null : Number(r.buyer_resale_target_nma),
    engineeringPv10PerNra: r.engineering_pv10_per_nra == null ? null : Number(r.engineering_pv10_per_nra),
    engineeringPv10PerNma: r.engineering_pv10_per_nma == null ? null : Number(r.engineering_pv10_per_nma),
    confidenceScore: r.confidence_score == null ? null : Number(r.confidence_score),
    confidenceLabel: r.confidence_label || '',
    productionScore: r.production_score == null ? null : Number(r.production_score),
    permitScore: r.permit_score == null ? null : Number(r.permit_score),
    leaseSaleScore: r.lease_sale_score == null ? null : Number(r.lease_sale_score),
    buyerCompScore: r.buyer_comp_score == null ? null : Number(r.buyer_comp_score),
    geologyScore: r.geology_score == null ? null : Number(r.geology_score),
    federalLandScore: r.federal_land_score == null ? null : Number(r.federal_land_score),
    wtiPrice: r.wti_price == null ? null : Number(r.wti_price),
    henryHubPrice: r.henry_hub_price == null ? null : Number(r.henry_hub_price),
    dataAsOf: r.data_as_of || null,
    computedAt: r.computed_at || null,
    staleAfter: r.stale_after || null,
    explanation: r.explanation || {},
    metrics: r.metrics || {},
    sourceCounts: r.source_counts || {},
    manualOverride: !!r.manual_override,
    overrideReason: r.override_reason || '',
    updatedAt: r.updated_at || null,
  }));
}

async function getSTRPricingData() {
  const { data, error } = await supabase.from('str_pricing').select('*');
  if (error) {
    // Table may not exist yet — fall back gracefully
    if (error.code === '42P01' || error.message?.includes('does not exist')) {
      console.warn('[supabaseStore] str_pricing table not found — using static fallback');
      return null;
    }
    handleError('getSTRPricingData', error);
  }
  return (data || []).map(r => ({
    id: r.id,
    section: r.section,
    township: r.township,
    range: r.range,
    tract: r.tract || null,
    price: Number(r.price),
    company: r.company || '',
    updatedAt: r.updated_at,
  }));
}

async function upsertSTRPricing(entry) {
  const uid = await currentUserId();
  const key = entry.tract
    ? `${entry.section}-${entry.township}-${entry.range}:${entry.tract}`
    : `${entry.section}-${entry.township}-${entry.range}`;
  const row = {
    id: key,
    section: entry.section,
    township: entry.township,
    range: entry.range,
    tract: entry.tract || null,
    price: entry.price,
    updated_by: uid,
  };
  if (entry.company !== undefined) row.company = entry.company || null;
  const { error } = await supabase.from('str_pricing').upsert(row);
  if (error) handleError('upsertSTRPricing', error);
}

async function deleteSTRPricing(id) {
  const { error } = await supabase.from('str_pricing').delete().eq('id', id);
  if (error) handleError('deleteSTRPricing', error);
}

// ── Tax automation (migration-044 / migration-045) ────────────────────────────
// Year-end IRS-form data: expense ledger lines that drive 1099-NEC,
// royalty payouts that drive 1099-MISC, partnership distributions that
// drive K-1, plus the cap-table (company_partners). All tables are
// admin-only RLS-gated, so non-admin callers will get an empty array
// from every list method below — that's by design.

function dbExpenseLedgerToApp(r) {
  if (!r) return null;
  return {
    id: r.id, statementId: r.statement_id,
    postedAt: r.posted_at, description: r.description,
    amount: Number(r.amount) || 0, category: r.category,
    recipientProfileId: r.recipient_profile_id,
    recipientContactId: r.recipient_contact_id,
    recipientBuyerId: r.recipient_buyer_id,
    notes: r.notes, createdAt: r.created_at,
  };
}
function appExpenseLedgerToDb(r) {
  return {
    statement_id: r.statementId || null,
    posted_at: sanitizeDate(r.postedAt),
    description: r.description || null,
    amount: Number(r.amount) || 0,
    category: r.category || 'Other',
    recipient_profile_id: r.recipientProfileId || null,
    recipient_contact_id: r.recipientContactId || null,
    recipient_buyer_id: r.recipientBuyerId || null,
    notes: r.notes || null,
  };
}

async function listExpenseLedger(year) {
  let q = supabase.from('expense_ledger').select('*').order('posted_at', { ascending: false });
  if (year) {
    q = q.gte('posted_at', `${year}-01-01`).lte('posted_at', `${year}-12-31`);
  }
  const { data, error } = await q;
  if (error) handleMissingTaxTableOrThrow('listExpenseLedger', error, 'expense_ledger');
  return (data || []).map(dbExpenseLedgerToApp);
}

async function addExpenseLedger(entry) {
  const { data, error } = await supabase
    .from('expense_ledger')
    .insert(appExpenseLedgerToDb(entry))
    .select().single();
  if (error) handleMissingTaxTableOrThrow('addExpenseLedger', error, 'expense_ledger');
  return dbExpenseLedgerToApp(data);
}

async function updateExpenseLedger(id, patch) {
  const { error } = await supabase
    .from('expense_ledger')
    .update(appExpenseLedgerToDb(patch))
    .eq('id', id);
  if (error) handleMissingTaxTableOrThrow('updateExpenseLedger', error, 'expense_ledger');
}

async function deleteExpenseLedger(id) {
  const { error } = await supabase.from('expense_ledger').delete().eq('id', id);
  if (error) handleMissingTaxTableOrThrow('deleteExpenseLedger', error, 'expense_ledger');
}

async function listExpenseStatements() {
  const { data, error } = await supabase
    .from('expense_statements')
    .select('*')
    .order('period_start', { ascending: false });
  if (error) handleMissingTaxTableOrThrow('listExpenseStatements', error, 'expense_statements');
  return data || [];
}

async function addExpenseStatement(entry) {
  const { data, error } = await supabase
    .from('expense_statements')
    .insert({
      period_start: sanitizeDate(entry.periodStart),
      period_end: sanitizeDate(entry.periodEnd),
      bank_name: entry.bankName || null,
      storage_path: entry.storagePath || null,
      imported_by: entry.importedBy || null,
      parsed_status: entry.parsedStatus || 'pending',
      notes: entry.notes || null,
    })
    .select().single();
  if (error) handleMissingTaxTableOrThrow('addExpenseStatement', error, 'expense_statements');
  return data;
}

function dbCommissionPayoutToApp(r) {
  if (!r) return null;
  return {
    id: r.id, dealId: r.deal_id,
    recipientProfileId: r.recipient_profile_id,
    recipientRole: r.recipient_role,
    payoutAmount: Number(r.payout_amount) || 0,
    paidOn: r.paid_on, notes: r.notes, createdAt: r.created_at,
  };
}

async function listCommissionPayouts(year) {
  let q = supabase.from('commission_payouts').select('*').order('created_at', { ascending: false });
  if (year) {
    // We aggregate by paid_on when populated, falling back to
    // created_at — but for the year filter we trust paid_on first
    // and pick up unpaid (paid_on null) rows separately at query time
    // when admin opts in. v1 keeps it simple — filter by paid_on year.
    q = q.gte('paid_on', `${year}-01-01`).lte('paid_on', `${year}-12-31`);
  }
  const { data, error } = await q;
  if (error) handleMissingTaxTableOrThrow('listCommissionPayouts', error, 'commission_payouts');
  return (data || []).map(dbCommissionPayoutToApp);
}

// Bulk insert used by the materializer when a deal hits Funds
// Disbursed. Uses `ignoreDuplicates: true` so re-running the
// materializer is a no-op (the unique index on
// (deal_id, recipient_role, recipient_profile_id) catches dupes).
async function insertCommissionPayouts(rows) {
  if (!rows?.length) return [];
  const payload = rows.map((r) => ({
    deal_id: r.dealId,
    recipient_profile_id: r.recipientProfileId || null,
    recipient_role: r.recipientRole,
    payout_amount: Number(r.payoutAmount) || 0,
    paid_on: sanitizeDate(r.paidOn) || null,
    notes: r.notes || null,
  }));
  const { data, error } = await supabase
    .from('commission_payouts')
    .upsert(payload, {
      onConflict: 'deal_id,recipient_role,recipient_profile_id',
      ignoreDuplicates: true,
    })
    .select();
  if (error) {
    if (isOnConflictConstraintError(error)) {
      console.warn('[supabaseStore] insertCommissionPayouts: falling back until migration-110/118 unique index is applied.');
      return insertCommissionPayoutsFallback(payload);
    }
    handleMissingTaxTableOrThrow('insertCommissionPayouts', error, 'commission_payouts');
  }
  return (data || []).map(dbCommissionPayoutToApp);
}

// Rebuild a deal's persisted split after its buyer changed: delete
// every payout row for the deal, then insert the freshly computed set
// (which may be empty — e.g. Paraclete purchase with no buy agent).
async function replaceCommissionPayoutsForDeal(dealId, rows) {
  if (!dealId) return [];
  const { error: delError } = await supabase
    .from('commission_payouts')
    .delete()
    .eq('deal_id', dealId);
  if (delError) handleMissingTaxTableOrThrow('replaceCommissionPayoutsForDeal', delError, 'commission_payouts');
  if (!rows?.length) return [];
  return insertCommissionPayouts(rows);
}

async function insertCommissionPayoutsFallback(payload) {
  const dealIds = Array.from(new Set(payload.map((row) => row.deal_id).filter(Boolean)));
  let existing = [];
  if (dealIds.length) {
    const { data, error } = await supabase
      .from('commission_payouts')
      .select('deal_id, recipient_role, recipient_profile_id')
      .in('deal_id', dealIds);
    if (error) handleMissingTaxTableOrThrow('insertCommissionPayoutsFallback.select', error, 'commission_payouts');
    existing = data || [];
  }
  const keyFor = (row) => `${row.deal_id}::${row.recipient_role}::${row.recipient_profile_id || ''}`;
  const seen = new Set(existing.map(keyFor));
  const missing = [];
  for (const row of payload) {
    const key = keyFor(row);
    if (seen.has(key)) continue;
    seen.add(key);
    missing.push(row);
  }
  if (!missing.length) return [];
  const { data, error } = await supabase
    .from('commission_payouts')
    .insert(missing)
    .select();
  if (error) handleMissingTaxTableOrThrow('insertCommissionPayoutsFallback.insert', error, 'commission_payouts');
  return (data || []).map(dbCommissionPayoutToApp);
}

async function updateCommissionPayout(id, patch) {
  const { error } = await supabase
    .from('commission_payouts')
    .update({
      payout_amount: patch.payoutAmount != null ? Number(patch.payoutAmount) : undefined,
      paid_on: patch.paidOn !== undefined ? sanitizeDate(patch.paidOn) : undefined,
      notes: patch.notes !== undefined ? patch.notes : undefined,
    })
    .eq('id', id);
  if (error) handleMissingTaxTableOrThrow('updateCommissionPayout', error, 'commission_payouts');
}

function dbRoyaltyPayoutToApp(r) {
  if (!r) return null;
  return {
    id: r.id, dealId: r.deal_id, contactId: r.contact_id,
    payoutAmount: Number(r.payout_amount) || 0,
    paidOn: r.paid_on, notes: r.notes, createdAt: r.created_at,
  };
}

async function listRoyaltyPayouts(year) {
  let q = supabase.from('royalty_payouts').select('*').order('created_at', { ascending: false });
  if (year) {
    q = q.gte('paid_on', `${year}-01-01`).lte('paid_on', `${year}-12-31`);
  }
  const { data, error } = await q;
  if (error) handleError('listRoyaltyPayouts', error);
  return (data || []).map(dbRoyaltyPayoutToApp);
}

async function insertRoyaltyPayouts(rows) {
  if (!rows?.length) return [];
  const payload = rows.map((r) => ({
    deal_id: r.dealId,
    contact_id: r.contactId,
    payout_amount: Number(r.payoutAmount) || 0,
    paid_on: sanitizeDate(r.paidOn) || null,
    notes: r.notes || null,
  }));
  const { data, error } = await supabase
    .from('royalty_payouts')
    .upsert(payload, {
      onConflict: 'deal_id,contact_id',
      ignoreDuplicates: true,
    })
    .select();
  if (error) handleError('insertRoyaltyPayouts', error);
  return (data || []).map(dbRoyaltyPayoutToApp);
}

async function updateRoyaltyPayout(id, patch) {
  const { error } = await supabase
    .from('royalty_payouts')
    .update({
      payout_amount: patch.payoutAmount != null ? Number(patch.payoutAmount) : undefined,
      paid_on: patch.paidOn !== undefined ? sanitizeDate(patch.paidOn) : undefined,
      notes: patch.notes !== undefined ? patch.notes : undefined,
    })
    .eq('id', id);
  if (error) handleError('updateRoyaltyPayout', error);
}

function dbCompanyPartnerToApp(r) {
  if (!r) return null;
  return {
    id: r.id, profileId: r.profile_id,
    equityPercent: Number(r.equity_percent) || 0,
    effectiveFrom: r.effective_from, effectiveTo: r.effective_to,
    notes: r.notes, createdAt: r.created_at,
  };
}

async function listCompanyPartners() {
  const { data, error } = await supabase
    .from('company_partners')
    .select('*')
    .order('effective_from', { ascending: false });
  if (error) handleError('listCompanyPartners', error);
  return (data || []).map(dbCompanyPartnerToApp);
}

async function addCompanyPartner(entry) {
  const { data, error } = await supabase
    .from('company_partners')
    .insert({
      profile_id: entry.profileId,
      equity_percent: Number(entry.equityPercent) || 0,
      effective_from: sanitizeDate(entry.effectiveFrom),
      effective_to: sanitizeDate(entry.effectiveTo) || null,
      notes: entry.notes || null,
    })
    .select().single();
  if (error) handleError('addCompanyPartner', error);
  return dbCompanyPartnerToApp(data);
}

async function updateCompanyPartner(id, patch) {
  const { error } = await supabase
    .from('company_partners')
    .update({
      profile_id: patch.profileId,
      equity_percent: patch.equityPercent != null ? Number(patch.equityPercent) : undefined,
      effective_from: patch.effectiveFrom !== undefined ? sanitizeDate(patch.effectiveFrom) : undefined,
      effective_to: patch.effectiveTo !== undefined ? (sanitizeDate(patch.effectiveTo) || null) : undefined,
      notes: patch.notes !== undefined ? patch.notes : undefined,
    })
    .eq('id', id);
  if (error) handleError('updateCompanyPartner', error);
}

async function deleteCompanyPartner(id) {
  const { error } = await supabase.from('company_partners').delete().eq('id', id);
  if (error) handleError('deleteCompanyPartner', error);
}

function dbPartnershipDistributionToApp(r) {
  if (!r) return null;
  return {
    id: r.id, taxYear: r.tax_year, profileId: r.profile_id,
    ordinaryBusinessIncome: Number(r.ordinary_business_income) || 0,
    royaltyIncome: Number(r.royalty_income) || 0,
    capitalContributed: Number(r.capital_contributed) || 0,
    cashDistributions: Number(r.cash_distributions) || 0,
    qbiAmount: Number(r.qbi_amount) || 0,
    notes: r.notes,
    createdAt: r.created_at, updatedAt: r.updated_at,
  };
}

async function listPartnershipDistributions(year) {
  let q = supabase.from('partnership_distributions').select('*');
  if (year) q = q.eq('tax_year', year);
  const { data, error } = await q;
  if (error) handleError('listPartnershipDistributions', error);
  return (data || []).map(dbPartnershipDistributionToApp);
}

async function upsertPartnershipDistribution(entry) {
  const { data, error } = await supabase
    .from('partnership_distributions')
    .upsert({
      tax_year: Number(entry.taxYear),
      profile_id: entry.profileId,
      ordinary_business_income: Number(entry.ordinaryBusinessIncome) || 0,
      royalty_income: Number(entry.royaltyIncome) || 0,
      capital_contributed: Number(entry.capitalContributed) || 0,
      cash_distributions: Number(entry.cashDistributions) || 0,
      qbi_amount: Number(entry.qbiAmount) || 0,
      notes: entry.notes || null,
    }, { onConflict: 'tenant_id,tax_year,profile_id' })
    .select().single();
  if (error) handleError('upsertPartnershipDistribution', error);
  return dbPartnershipDistributionToApp(data);
}

function dbTaxRecipientInfoToApp(r) {
  if (!r) return null;
  return {
    profileId: r.profile_id,
    taxClassification: r.tax_classification,
    businessName: r.business_name,
    tinFull: r.tin_full,
    updatedAt: r.updated_at,
  };
}

async function listTaxRecipientInfo() {
  const { data, error } = await supabase.from('tax_recipient_info').select('*');
  if (error) handleError('listTaxRecipientInfo', error);
  return (data || []).map(dbTaxRecipientInfoToApp);
}

// ── Exported store (Supabase if configured, localStorage otherwise) ────────────

const supabaseStoreMethods = {
  seedDefaults: () => Promise.resolve(),

  // Users
  getUsers, addUser, updateUser, deleteUser,

  // Assignments (deprecated — only getAssignments kept for legacy read-path during Phase D transition)
  getAssignments,

  // Flowcharts
  getFlowcharts, addFlowchart, updateFlowchart, deleteFlowchart,

  // Documents
  getDocuments, getAllDocuments, getDocumentsForDeal, searchDocuments, getDocumentCount,
  addDocument, addDocumentsBatch, updateDocument, deleteDocument,
  uploadFileDirectly, deleteStoragePath,

  // Contacts
  getContacts, addContact, updateContact, deleteContact,

  // Ownership
  getOwnership, getOwnershipByAssignment, getOwnershipForEstimate,
  saveOwnership, deleteOwnership,

  // Deals (Pipeline)
  getDeals, getPassedDeals, getDealFull, addDeal, updateDeal, deleteDeal, claimDeal,
  recordAuditEvent,

  // Deals — normalized children (migration-028)
  getDealTodos, addDealTodo, updateDealTodo, deleteDealTodo,
  getDealActivities, getDealActivityStamps, getDealActivitiesForDeal,
  getDealStateSummaries,
  addDealActivity, deleteDealActivity,
  getDealActivityAcks, addDealActivityAck, removeDealActivityAck,
  getDealSignatureRequests, getDealSignatureRequestsForDeal,
  upsertDealSignatureRequest, deleteDealSignatureRequest,
  getCertifiedMailJobs, updateCertifiedMailJob,
  // Realtime payload converters — used by AppContext to translate
  // postgres_changes payloads into the camelCase shape the UI uses.
  dbTodoToApp, dbActivityToApp, dbSignatureRequestToApp,
  dbCertifiedMailJobToApp, dbCertifiedMailDocumentToApp, dbCertifiedMailEventToApp,
  dbDealStateSummaryToApp,

  // Offers
  getOffers, addOffer, updateOffer, deleteOffer,

  // Buyers
  getBuyers, addBuyer, updateBuyer, deleteBuyer,
  getInvestors, addInvestor, updateInvestor, deleteInvestor,
  getInvestorEntities, addInvestorEntity, updateInvestorEntity, deleteInvestorEntity,
  getAssetIncome, addAssetIncome, updateAssetIncome, deleteAssetIncome,

  // Packages
  getPackages, addPackage, updatePackage, deletePackage,

  // Closings
  getClosings, addClosing, updateClosing, deleteClosing,

  // Closing Tasks
  getClosingTasks, addClosingTask, updateClosingTask, deleteClosingTask,

  // STR Pricing
  getSTRPricingData, upsertSTRPricing, deleteSTRPricing,
  getNmSTRPricingReferences,
  getNotifications, markNotificationRead, markAllNotificationsRead, deleteNotification,
  registerPushDeviceToken, listPushDeviceTokens, retirePushDeviceToken,

  // Tax automation (migration-044 / migration-045)
  listExpenseLedger, addExpenseLedger, updateExpenseLedger, deleteExpenseLedger,
  listExpenseStatements, addExpenseStatement,
  listCommissionPayouts, insertCommissionPayouts, replaceCommissionPayoutsForDeal, updateCommissionPayout,
  listRoyaltyPayouts, insertRoyaltyPayouts, updateRoyaltyPayout,
  listCompanyPartners, addCompanyPartner, updateCompanyPartner, deleteCompanyPartner,
  listPartnershipDistributions, upsertPartnershipDistribution,
  listTaxRecipientInfo,
};

const mobilePushLocalFallback = {
  registerPushDeviceToken: async () => ({ skipped: true, reason: 'supabase-disabled' }),
  listPushDeviceTokens: async () => [],
  retirePushDeviceToken: async () => null,
};

export const store = isSupabaseEnabled
  ? supabaseStoreMethods
  : { ...localStore, ...mobilePushLocalFallback };
